<template>
  <div class="chat-page-fullwidth">
    <!-- Login Prompt -->
    <div v-if="!isLoggedIn" class="chat-login-card">
      <div class="mb-4"><i class="bi bi-chat-dots fs-1 text-muted"></i></div>
      <div class="fw-bold fs-4 mb-3">
        {{ $t("chat.please_login") || "Please login to access messages" }}
      </div>
      <router-link class="btn btn-success" :to="{ name: 'Login' }">{{
        $t("common.login") || "Login"
      }}</router-link>
    </div>

    <!-- Chat Master Layout -->
    <div v-else class="chat-master">
      <div class="chat-list">
        <!-- Chat List Header with Actions -->
        <div class="chat-list-header">
          <div class="chat-list-header-title">
            {{ $t("menu.messages") || "Messages" }}
          </div>
          <div class="chat-list-header-actions">
            <button
              class="chat-header-btn"
              @click="fetchConversations"
              :title="$t('common.refresh') || 'Refresh'"
            >
              <i class="bi bi-arrow-clockwise"></i>
            </button>
            <!-- Actions Menu -->
            <div class="chat-actions-menu">
              <button
                class="chat-header-btn"
                @click="toggleActionsMenu"
                ref="menuTrigger"
                :title="$t('common.more') || 'More'"
              >
                <i class="bi bi-three-dots-vertical"></i>
                <span v-if="pendingRequestsCount > 0" class="menu-badge">{{
                  pendingRequestsCount
                }}</span>
              </button>
              <transition name="menu-fade">
                <div
                  v-if="showActionsMenu"
                  class="chat-menu-dropdown"
                  ref="menuDropdown"
                >
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('qr')"
                  >
                    <i class="bi bi-qr-code"></i>
                    <span>{{ $t("chat.my_qr_code") || "My QR Code" }}</span>
                  </button>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('requests')"
                  >
                    <i class="bi bi-envelope"></i>
                    <span>{{
                      $t("chat.message_requests") || "Message Requests"
                    }}</span>
                    <span
                      v-if="pendingRequestsCount > 0"
                      class="menu-item-badge"
                      >{{ pendingRequestsCount }}</span
                    >
                  </button>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('addFriend')"
                  >
                    <i class="bi bi-person-plus"></i>
                    <span>{{ $t("chat.add_friend") || "Add Friend" }}</span>
                  </button>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('createGroup')"
                  >
                    <i class="bi bi-people"></i>
                    <span>{{ $t("chat.create_group") || "Create Group" }}</span>
                  </button>
                  <div class="chat-menu-divider"></div>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('friendsList')"
                  >
                    <i class="bi bi-person-lines-fill"></i>
                    <span>{{ $t("chat.friends_list") || "Friends List" }}</span>
                  </button>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('blockedUsers')"
                  >
                    <i class="bi bi-slash-circle"></i>
                    <span>{{
                      $t("chat.blocked_users") || "Blocked Users"
                    }}</span>
                  </button>
                  <div class="chat-menu-divider"></div>
                  <button
                    class="chat-menu-item"
                    @click="handleMenuAction('allChats')"
                  >
                    <i class="bi bi-archive"></i>
                    <span>{{ $t("chat.all_chats") || "All Chats" }}</span>
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <!-- Filter Tabs (Flutter sync: ConversationFilter) -->
        <div class="chat-filter-tabs">
          <button
            class="chat-filter-tab"
            :class="{ active: conversationFilter === 'all' }"
            @click="conversationFilter = 'all'"
          >
            {{ $t("chat.filter_all") || "All" }}
          </button>
          <button
            class="chat-filter-tab"
            :class="{ active: conversationFilter === 'unread' }"
            @click="conversationFilter = 'unread'"
          >
            {{ $t("chat.filter_unread") || "Unread" }}
          </button>
          <button
            class="chat-filter-tab"
            :class="{ active: conversationFilter === 'groups' }"
            @click="conversationFilter = 'groups'"
          >
            {{ $t("chat.filter_groups") || "Groups" }}
          </button>
          <button
            class="chat-filter-tab"
            :class="{ active: conversationFilter === 'direct' }"
            @click="conversationFilter = 'direct'"
          >
            {{ $t("chat.filter_direct") || "Direct" }}
          </button>
        </div>
        <!-- Search Field - Opens Advanced Search Page -->
        <div class="chat-search chat-search--clickable" @click="openSearchPage">
          <i class="bi bi-search chat-search__icon"></i>
          <span class="chat-search__placeholder">
            {{ $t('chat.search_conversations') || 'Search conversations...' }}
          </span>
        </div>
        <div class="chat-list-body">
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-success" role="status"></div>
            <div class="mt-3">{{ $t("common.loading") || "Loading..." }}</div>
          </div>
          <div v-else-if="error" class="alert alert-danger m-3">
            <div class="d-flex align-items-center">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <div class="flex-grow-1">{{ error }}</div>
              <button
                class="btn btn-sm btn-light-danger ms-3"
                @click="fetchConversations"
              >
                {{ $t("common.retry") || "Retry" }}
              </button>
            </div>
          </div>
          <template v-else>
            <div
              v-if="filteredConversations.length === 0"
              class="p-10 text-center text-muted"
            >
              {{
                conversationFilter === "all"
                  ? $t("chat.no_conversations") || "No conversations yet"
                  : $t("chat.no_matching_conversations") ||
                    "No matching conversations"
              }}
            </div>
            <div v-else class="list-group list-group-flush">
              <a
                v-for="c in filteredConversations"
                :key="c.id"
                href="#"
                class="list-group-item list-group-item-action py-3"
                :class="{
                  active:
                    String(selectedId) ===
                    String(c.talkjs_conversation_id || c.id),
                  'conv-unread': isConversationUnread(c),
                }"
                @click.prevent="selectConversation(c)"
              >
                <div class="d-flex align-items-center">
                  <div class="symbol symbol-40px me-3 position-relative">
                    <img
                      :src="getConversationAvatar(c)"
                      @error="onImgError"
                      alt="avatar"
                    />
                    <span
                      v-if="isConversationUnread(c)"
                      class="conv-unread-dot"
                    ></span>
                  </div>
                  <div class="flex-grow-1 overflow-hidden">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <div
                        class="d-flex align-items-center gap-1"
                        :class="
                          isConversationUnread(c) ? 'fw-bold' : 'fw-semibold'
                        "
                        style="max-width: 180px"
                      >
                        <span class="conv-title-text">{{ displayName(c) }}</span>
                        <!-- Mute indicator beside title -->
                        <i
                          v-if="isConversationMuted(c)"
                          class="bi bi-bell-slash conv-muted-icon"
                          :title="$t('chat.muted_conversation') || 'Muted Conversation'"
                        ></i>
                      </div>
                      <span
                        v-if="getUnreadCount(c) > 0"
                        class="badge bg-success"
                        >{{
                          getUnreadCount(c) > 99 ? "99+" : getUnreadCount(c)
                        }}</span
                      >
                    </div>
                    <div
                      class="text-truncate"
                      :class="
                        isConversationUnread(c)
                          ? 'conv-preview-unread'
                          : 'text-muted'
                      "
                      style="max-width: 220px"
                    >
                      {{ lastMessagePreview(c) }}
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </template>
        </div>
      </div>

      <div class="chat-panel">
        <div v-if="selectedId" class="chat-panel-header">
          <div class="chat-panel-header__left">
            <div class="symbol symbol-35px flex-shrink-0">
              <img
                :src="
                  selectedConversation
                    ? getConversationAvatar(selectedConversation)
                    : '/assets/img/acad-icon.png'
                "
                @error="onImgError"
                alt="avatar"
              />
            </div>
            <div class="chat-panel-header__info">
              <div class="chat-panel-header__name">
                {{
                  selectedConversation
                    ? displayName(selectedConversation)
                    : $t("common.chat") || "Chat"
                }}
              </div>
              <div class="chat-panel-header__status">
                {{ $t("chat.last_seen_recently") || "last seen recently" }}
              </div>
            </div>
          </div>
          <div class="chat-panel-header__actions">
            <!-- Members Button - Hidden until further notice -->
            <!-- <button class="chat-panel-action-btn" @click="openMembersModal" :title="$t('chat.conversation_members') || 'أعضاء المحادثة'">
                  <i class="bi bi-people"></i>
                </button> -->
            <!-- Search in Chat Button -->
            <button
              class="chat-panel-action-btn"
              :class="{ active: isSearchMode }"
              @click="toggleConversationSearch"
              :title="$t('chat.search_in_chat') || 'البحث في المحادثة'"
            >
              <i class="bi bi-search"></i>
            </button>
            <!-- Mia Summary Button -->
            <button
              class="chat-panel-action-btn chat-panel-action-btn--mia"
              @click="openPanelSummary"
              :title="$t('chat.summarize_chat') || 'Summarize with Mia'"
            >
              <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon" />
            </button>
            <!-- Admin Actions (only for group owners) -->
            <button
              v-if="panelIsGroupChat && panelIsOwner"
              class="chat-panel-action-btn"
              @click="showActionModal = true"
              :title="$t('chat.admin_settings') || 'Admin Settings'"
            >
              <i class="bi bi-shield"></i>
            </button>
            <!-- Member Actions (for all users) -->
            <button
              class="chat-panel-action-btn"
              @click="showPanelMemberMenu = true"
              :title="$t('common.more') || 'More'"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>
          </div>
        </div>
        <div v-if="!selectedId" class="chat-placeholder text-center text-muted">
          <i class="bi bi-chat-dots fs-1 d-block mb-3"></i>
          <div class="fw-semibold">
            {{ $t("chat.no_conversations") || "No conversations yet" }}
          </div>
          <div>{{ $t("common.message") || "Message" }}</div>
        </div>
        <div v-else class="chat-panel-inner">
          <ChatConversation
            ref="conversationComponent"
            :conversationId="selectedId"
            :embedded="true"
            @muted="onConversationMuted"
            @unmuted="onConversationUnmuted"
            @search-toggled="isSearchMode = $event"
          />
        </div>
      </div>
    </div>

    <!-- QR Code Dialog -->
    <QrCodeDialog v-if="showQrCodeDialog" @close="showQrCodeDialog = false" />

    <!-- Add Friend Dialog -->
    <AddFriendDialog
      v-if="showAddFriendDialog"
      @close="showAddFriendDialog = false"
      @request-sent="onRequestSent"
    />

    <!-- Chat Action Modal (Admin Moderation) -->
    <ChatActionModal
      v-if="showActionModal"
      :visible="showActionModal"
      :title="$t('chat.admin_settings') || 'Admin Settings'"
      :actions="panelActions"
      @close="showActionModal = false"
      @action="handlePanelAction"
    />

    <!-- Conversation Members Modal (View / Kick / Add modes) -->
    <transition name="fade">
      <div
        v-if="showMembersModal"
        class="chat-members-overlay"
        @click.self="closeMembersModal"
      >
        <div class="chat-members-dialog">
          <div class="chat-members-dialog__header">
            <i :class="membersModalMode === 'add' ? 'bi bi-person-plus-fill' : membersModalMode === 'kick' ? 'bi bi-person-dash-fill' : 'bi bi-people-fill'"></i>
            <span>{{
              membersModalMode === 'kick' 
                ? ($t("chat.kick_student") || "Kick Student")
                : membersModalMode === 'add'
                  ? ($t("chat.add_student") || "Add Student")
                  : ($t("chat.conversation_members") || "Conversation Members")
            }}</span>
            <button
              class="chat-members-dialog__close"
              @click="closeMembersModal"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="chat-members-dialog__body">
            <!-- Loading State -->
            <div v-if="membersLoading" class="chat-members-loading">
              <div class="spinner-border text-success" role="status"></div>
              <span>{{ $t("common.loading") }}</span>
            </div>
            <!-- Error State -->
            <div v-else-if="membersError" class="chat-members-error">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span>{{ membersError }}</span>
            </div>
            <!-- Members/Friends List -->
            <div v-else class="chat-members-list">
              <!-- Add Mode: Show Friends -->
              <template v-if="membersModalMode === 'add'">
                <div v-if="friendsListForAdd.length === 0" class="chat-members-empty">
                  <i class="bi bi-person-plus"></i>
                  <span>{{ $t("chat.no_friends_to_add") || "No friends to add" }}</span>
                </div>
                <div v-else class="chat-members-grid">
                  <div
                    v-for="friend in friendsListForAdd"
                    :key="friend.id"
                    class="chat-member-card chat-member-card--selectable"
                    @click="addStudent(friend)"
                  >
                    <div class="chat-member-card__avatar-wrapper">
                      <img
                        :src="friend.avatar || '/assets/img/acad-icon.png'"
                        @error="onImgError"
                        class="chat-member-card__avatar"
                        alt=""
                      />
                    </div>
                    <div class="chat-member-card__info">
                      <div class="chat-member-card__name">{{ friend.name }}</div>
                      <div class="chat-member-card__email">{{ friend.email }}</div>
                    </div>
                    <div class="chat-member-card__action chat-member-card__action--add">
                      <i class="bi bi-plus-circle-fill"></i>
                    </div>
                  </div>
                </div>
              </template>
              <!-- Kick Mode: Show Members (excluding self and admin) -->
              <template v-else-if="membersModalMode === 'kick'">
                <div v-if="kickableMembers.length === 0" class="chat-members-empty">
                  <i class="bi bi-person-dash"></i>
                  <span>{{ $t("chat.no_students_to_kick") || "No students to kick" }}</span>
                </div>
                <div v-else class="chat-members-grid">
                  <div
                    v-for="member in kickableMembers"
                    :key="member.id"
                    class="chat-member-card chat-member-card--selectable chat-member-card--danger"
                    @click="kickStudent(member)"
                  >
                    <div class="chat-member-card__avatar-wrapper">
                      <img
                        :src="member.avatar || '/assets/img/acad-icon.png'"
                        @error="onImgError"
                        class="chat-member-card__avatar"
                        alt=""
                      />
                    </div>
                    <div class="chat-member-card__info">
                      <div class="chat-member-card__name">{{ member.name }}</div>
                      <div class="chat-member-card__email">{{ member.email }}</div>
                    </div>
                    <div class="chat-member-card__action chat-member-card__action--kick">
                      <i class="bi bi-x-circle-fill"></i>
                    </div>
                  </div>
                </div>
              </template>
              <!-- View Mode: Show All Members -->
              <template v-else>
                <div v-if="membersList.length === 0" class="chat-members-empty">
                  <i class="bi bi-people"></i>
                  <span>{{ $t("chat.no_members") }}</span>
                </div>
                <div v-else>
                  <!-- Metadata Header -->
                  <div class="chat-members-header">
                    <div class="chat-members-header__info">
                      <div class="chat-members-header__type">
                        <i :class="getConversationTypeIcon"></i>
                        <span>{{ getMembersTypeLabel }}</span>
                      </div>
                      <div class="chat-members-header__count">
                        <i class="bi bi-people-fill"></i>
                        <span
                          >{{ membersMetadata.total_members }}
                          {{
                            $t("chat.member", membersMetadata.total_members)
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <!-- Members Grid -->
                  <div class="chat-members-grid">
                    <div
                      v-for="member in membersList"
                      :key="member.id"
                      class="chat-member-card"
                      :class="{
                        'chat-member-card--current': member.is_current_user,
                        'chat-member-card--admin': member.is_admin,
                      }"
                    >
                      <div class="chat-member-card__avatar-wrapper">
                        <img
                          :src="member.avatar || '/assets/img/acad-icon.png'"
                          @error="onImgError"
                          class="chat-member-card__avatar"
                          alt=""
                        />
                        <div
                          v-if="member.is_admin"
                          class="chat-member-card__admin-badge"
                        >
                          <i class="bi bi-shield-fill-check"></i>
                        </div>
                      </div>
                      <div class="chat-member-card__info">
                        <div class="chat-member-card__name">
                          {{ member.name }}
                        </div>
                        <div class="chat-member-card__email">
                          {{ member.email }}
                        </div>
                        <div class="chat-member-card__badges">
                          <span
                            v-if="member.is_current_user"
                            class="chat-member-badge chat-member-badge--you"
                          >
                            <i class="bi bi-person-check-fill"></i>
                            {{ $t("chat.you") }}
                          </span>
                          <span
                            v-if="member.is_admin"
                            class="chat-member-badge chat-member-badge--admin"
                          >
                            <i class="bi bi-shield-fill"></i>
                            {{ $t("chat.admin") }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Mia Summary Dialog (Flutter sync: _showChatSummary) -->
    <transition name="fade">
      <div
        v-if="showPanelSummaryDialog"
        class="chat-summary-overlay"
        @click.self="showPanelSummaryDialog = false"
      >
        <div class="chat-summary-dialog">
          <div class="chat-summary-dialog__header">
            <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
            <span>{{
              $t("chat.summarize_chat_mia") || "Summarize Chat with Mia"
            }}</span>
            <button
              class="chat-summary-dialog__close"
              @click="showPanelSummaryDialog = false"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div class="chat-summary-dialog__body">
            <div class="chat-summary-presets">
              <button
                class="chat-summary-chip"
                :class="{ active: panelSummaryPreset === 'today' }"
                @click="selectPanelSummaryPreset('today')"
              >
                {{ $t("chat.today") || "Today" }}
              </button>
              <button
                class="chat-summary-chip"
                :class="{ active: panelSummaryPreset === 'yesterday' }"
                @click="selectPanelSummaryPreset('yesterday')"
              >
                {{ $t("chat.yesterday") || "Yesterday" }}
              </button>
              <button
                class="chat-summary-chip"
                :class="{ active: panelSummaryPreset === 'custom' }"
                @click="selectPanelSummaryPreset('custom')"
              >
                {{ $t("chat.custom") || "Custom" }}
              </button>
            </div>
            <div
              v-if="panelSummaryPreset === 'custom'"
              class="chat-summary-dates"
            >
              <label class="chat-summary-date-label">
                <i class="bi bi-calendar3"></i>
                {{ $t("chat.start_date") || "Start Date" }}
                <input
                  type="date"
                  v-model="panelSummaryStartDate"
                  class="chat-summary-date-input"
                  :max="panelSummaryEndDate || todayStr"
                />
              </label>
              <label class="chat-summary-date-label">
                <i class="bi bi-calendar3"></i>
                {{ $t("chat.end_date") || "End Date" }}
                <input
                  type="date"
                  v-model="panelSummaryEndDate"
                  class="chat-summary-date-input"
                  :min="panelSummaryStartDate"
                  :max="todayStr"
                />
              </label>
            </div>
            <div v-if="panelSummaryLoading" class="chat-summary-loading">
              <div
                class="spinner-border spinner-border-sm text-success"
                role="status"
              ></div>
              <span>{{
                $t("chat.generating_summary") || "Generating summary..."
              }}</span>
            </div>
            <div v-else-if="panelSummaryResult" class="chat-summary-result">
              <div class="chat-summary-result__header">
                <div class="chat-summary-result__title">
                  <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
                  <div class="chat-summary-result__title-text">
                    <span>{{ $t("chat.chat_summary") || "Chat Summary" }}</span>
                    <span
                      v-if="panelSummaryMessageCount"
                      class="chat-summary-result__count"
                      >{{ panelSummaryMessageCount }}
                      {{ $t("chat.messages") || "messages" }}</span
                    >
                  </div>
                </div>
                <div class="chat-summary-result__actions">
                  <button
                    class="chat-summary-action-btn"
                    @click="panelCopySummary"
                    :title="$t('chat.copy_summary') || 'Copy Summary'"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                  <button
                    class="chat-summary-action-btn"
                    @click="panelShareSummary"
                    :title="$t('chat.share_summary') || 'Share Summary'"
                  >
                    <i class="bi bi-share"></i>
                  </button>
                </div>
              </div>
              <div class="chat-summary-result__text">
                {{ panelSummaryResult }}
              </div>
              <div
                v-if="panelSummaryMetadata"
                class="chat-summary-result__footer"
              >
                <div class="chat-summary-mia-badge">
                  <img src="/assets/img/mia_icon.png" alt="Mia" class="mia-icon-small" />
                  <span>{{
                    $t("chat.created_by_mia") || "Created by Mia AI"
                  }}</span>
                </div>
              </div>
            </div>
            <div v-else-if="panelSummaryError" class="chat-summary-error">
              <i class="bi bi-exclamation-triangle"></i>
              <span>{{ panelSummaryError }}</span>
            </div>
          </div>
          <div class="chat-summary-dialog__footer">
            <button
              class="chat-summary-btn--cancel"
              @click="showPanelSummaryDialog = false"
            >
              {{ $t("common.cancel") || "Cancel" }}
            </button>
            <button
              class="chat-summary-btn--submit"
              :disabled="panelSummaryLoading || panelSummaryGenerated"
              @click="panelGenerateSummary"
            >
              <i class="fas fa-magic"></i>
              {{ $t("chat.summarize") || "Summarize" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- All Chats Modal (Flutter sync: _showAllConversations → ChatEndpoints.allConversations) -->
    <transition name="fade">
      <div
        v-if="showAllChatsModal"
        class="all-chats-overlay"
        @click.self="closeAllChatsModal"
      >
        <div class="all-chats-dialog">
          <div class="all-chats-dialog__header">
            <i class="bi bi-archive"></i>
            <span>{{ $t('chat.all_chats') || 'All Chats' }}</span>
            <button class="all-chats-dialog__close" @click="closeAllChatsModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Search inside all chats -->
          <div class="all-chats-search">
            <i class="bi bi-search all-chats-search__icon"></i>
            <input
              v-model="allChatsSearch"
              type="text"
              class="all-chats-search__input"
              :placeholder="$t('chat.search_conversations') || 'Search...'"
              @input="onAllChatsSearch"
            />
            <div v-if="allChatsSearchLoading" class="chat-search__spinner"></div>
            <button
              v-else-if="allChatsSearch"
              class="chat-search__clear"
              @click="allChatsSearch = ''; allChatsFiltered = allChats;"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>

          <div class="all-chats-dialog__body">
            <!-- Loading -->
            <div v-if="allChatsLoading" class="all-chats-loading">
              <div class="all-chats-spinner"></div>
              <span>{{ $t('common.loading') || 'Loading...' }}</span>
            </div>
            <!-- Error -->
            <div v-else-if="allChatsError" class="all-chats-error">
              <i class="bi bi-exclamation-triangle"></i>
              <span>{{ allChatsError }}</span>
              <button @click="loadAllChats">{{ $t('common.retry') || 'Retry' }}</button>
            </div>
            <!-- Empty -->
            <div v-else-if="allChatsFiltered.length === 0" class="all-chats-empty">
              <i class="bi bi-chat-square-x"></i>
              <span>{{ $t('chat.no_conversations') || 'No conversations found' }}</span>
            </div>
            <!-- List -->
            <div v-else class="all-chats-list">
              <a
                v-for="c in allChatsFiltered"
                :key="c.id"
                href="#"
                class="all-chats-item"
                :class="{ 'all-chats-item--deleted': c.is_deleted || c.isDeleted }"
                @click.prevent="openAllChatsConversation(c)"
              >
                <div class="all-chats-item__avatar">
                  <img
                    v-if="getConversationAvatar(c)"
                    :src="getConversationAvatar(c)"
                    @error="onImgError"
                    alt=""
                  />
                  <div v-else class="all-chats-item__avatar-placeholder">
                    {{ displayNameForAllChats(c).charAt(0).toUpperCase() }}
                  </div>
                  <span v-if="c.is_deleted || c.isDeleted" class="all-chats-item__deleted-badge">
                    <i class="bi bi-trash3"></i>
                  </span>
                </div>
                <div class="all-chats-item__info">
                  <div class="all-chats-item__name">{{ displayNameForAllChats(c) }}</div>
                  <div class="all-chats-item__meta">
                    <span v-if="c.is_deleted || c.isDeleted" class="all-chats-item__deleted-label">
                      {{ $t('chat.deleted') || 'Deleted' }}
                    </span>
                    <span v-else class="all-chats-item__preview">{{ lastMessagePreview(c) }}</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Member Actions Modal (Bottom Sheet Style) -->
    <transition name="modal-fade">
      <div v-if="showPanelMemberMenu" class="member-actions-overlay" @click.self="showPanelMemberMenu = false">
        <transition name="modal-slide">
          <div v-if="showPanelMemberMenu" class="member-actions-modal">
            <div class="member-actions-modal__header">
              <div class="member-actions-modal__handle"></div>
              <h3 class="member-actions-modal__title">{{ $t('chat.chat_options') || 'Chat Options' }}</h3>
            </div>
            <div class="member-actions-modal__body">
              <!-- Mute/Unmute -->
              <button class="member-actions-modal__item" @click="panelToggleMute">
                <div class="member-actions-modal__icon" :class="{ 'member-actions-modal__icon--muted': panelIsMuted }">
                  <i :class="panelIsMuted ? 'bi bi-bell' : 'bi bi-bell-slash'"></i>
                </div>
                <div class="member-actions-modal__text">
                  <span class="member-actions-modal__label">
                    {{ panelIsMuted 
                      ? ($t('chat.unmute_chat') || 'Unmute Chat')
                      : ($t('chat.mute_chat') || 'Mute Chat')
                    }}
                  </span>
                  <span class="member-actions-modal__desc">
                    {{ panelIsMuted 
                      ? ($t('chat.unmute_desc') || 'Enable notifications for this chat')
                      : ($t('chat.mute_desc') || 'Disable notifications for this chat')
                    }}
                  </span>
                </div>
                <i class="bi bi-chevron-right member-actions-modal__arrow"></i>
              </button>
              <!-- Block User (only for DMs, not blocked yet) -->
              <button
                v-if="!panelIsGroupChat && !panelIsBlocked"
                class="member-actions-modal__item member-actions-modal__item--danger"
                @click="panelBlockUser"
              >
                <div class="member-actions-modal__icon member-actions-modal__icon--danger">
                  <i class="bi bi-slash-circle"></i>
                </div>
                <div class="member-actions-modal__text">
                  <span class="member-actions-modal__label">{{ $t('chat.block_user') || 'Block User' }}</span>
                  <span class="member-actions-modal__desc">{{ $t('chat.block_description') || 'Block this user from contacting you' }}</span>
                </div>
                <i class="bi bi-chevron-right member-actions-modal__arrow"></i>
              </button>
              <!-- Unblock User (only for DMs, already blocked by current user) -->
              <button
                v-if="!panelIsGroupChat && panelIsBlocked && panelIsUserBlocker"
                class="member-actions-modal__item"
                @click="panelUnblockUser"
              >
                <div class="member-actions-modal__icon">
                  <i class="bi bi-check-circle"></i>
                </div>
                <div class="member-actions-modal__text">
                  <span class="member-actions-modal__label">{{ $t('chat.unblock_user') || 'Unblock User' }}</span>
                  <span class="member-actions-modal__desc">{{ $t('chat.unblock_description') || 'Allow this user to contact you again' }}</span>
                </div>
                <i class="bi bi-chevron-right member-actions-modal__arrow"></i>
              </button>
              <!-- Leave Group (only for groups) -->
              <button 
                v-if="panelIsGroupChat" 
                class="member-actions-modal__item member-actions-modal__item--danger"
                @click="panelLeaveGroup"
              >
                <div class="member-actions-modal__icon member-actions-modal__icon--danger">
                  <i class="bi bi-box-arrow-right"></i>
                </div>
                <div class="member-actions-modal__text">
                  <span class="member-actions-modal__label">{{ $t('chat.leave_group') || 'Leave Group' }}</span>
                  <span class="member-actions-modal__desc">{{ $t('chat.leave_group_desc') || 'You will no longer receive messages from this group' }}</span>
                </div>
                <i class="bi bi-chevron-right member-actions-modal__arrow"></i>
              </button>
              <!-- Delete Conversation -->
              <button 
                class="member-actions-modal__item member-actions-modal__item--danger"
                @click="panelDeleteConversation"
              >
                <div class="member-actions-modal__icon member-actions-modal__icon--danger">
                  <i class="bi bi-trash"></i>
                </div>
                <div class="member-actions-modal__text">
                  <span class="member-actions-modal__label">{{ $t('chat.delete_chat') || 'Delete Chat' }}</span>
                  <span class="member-actions-modal__desc">{{ $t('chat.delete_chat_desc') || 'Delete this conversation permanently' }}</span>
                </div>
                <i class="bi bi-chevron-right member-actions-modal__arrow"></i>
              </button>
            </div>
            <div class="member-actions-modal__footer">
              <button class="member-actions-modal__cancel" @click="showPanelMemberMenu = false">
                {{ $t('common.cancel') || 'Cancel' }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import ChatConversation from "./Conversation.vue";
import { ChatService } from "@/services/chat";
import { chatState, upsertConversation, updateLastMessage, bumpConversationToTop } from "@/stores/chat";
import { pusherService } from "@/services/pusher";
import QrCodeDialog from "@/components/chat/QrCodeDialog.vue";
import AddFriendDialog from "@/components/chat/AddFriendDialog.vue";
import ChatActionModal from "@/components/chat/ChatActionModal.vue";
import Swal from "sweetalert2";
import { getSwalConfig } from "@/utils/swal-config";

export default {
  name: "ChatMaster",
  components: { ChatConversation, QrCodeDialog, AddFriendDialog, ChatActionModal },
  data() {
    return {
      loading: false,
      error: null,
      token: localStorage.getItem("access_token"),
      selectedIdLocal: null,
      showQrCodeDialog: false,
      showAddFriendDialog: false,
      showActionModal: false,
      pendingRequestsCount: 0,
      showActionsMenu: false,
      conversationFilter: "all", // Flutter sync: ConversationFilter enum (all, unread, groups, direct)
      // Pusher subscriptions for real-time updates
      pusherSubscriptions: [], // Array of unsubscribe functions
      // Polling fallback for real-time updates
      pollingInterval: null,
      // All Chats modal (Flutter sync: _showAllConversations)
      showAllChatsModal: false,
      allChats: [],
      allChatsFiltered: [],
      allChatsLoading: false,
      allChatsError: null,
      allChatsSearch: '',
      allChatsSearchLoading: false,
      allChatsSearchDebounce: null,
      // Panel header state (for embedded conversation actions)
      showPanelMenu: false,
      showPanelMemberMenu: false,
      panelDropdownPos: { top: 0, right: 0 },
      isSearchMode: false,
      panelMetaLoaded: false, // Guard: true only after conversation meta is loaded
      panelIsGroupChat: false,
      panelIsBlocked: false,
      panelIsUserBlocker: false,
      panelIsOwner: false,
      panelIsLocked: false,
      panelIsMuted: false,
      // Panel Mia Summary dialog
      showPanelSummaryDialog: false,
      panelSummaryPreset: "today",
      panelSummaryStartDate: "",
      panelSummaryEndDate: "",
      panelSummaryLoading: false,
      panelSummaryResult: null,
      panelSummaryError: null,
      panelSummaryMessageCount: null,
      panelSummaryMetadata: null,
      panelSummaryGenerated: false,
      // Members Modal
      showMembersModal: false,
      membersLoading: false,
      membersError: null,
      membersList: [],
      membersMetadata: {
        conversation_id: null,
        type: null,
        course_id: null,
        creator_id: null,
        admin_id: null,
        total_members: 0,
      },
      membersModalMode: 'view', // 'view', 'kick', 'add'
      friendsListForAdd: [],
      // Cached conversation for deleted chats (loaded via API)
      cachedConversation: null,
    };
  },
  computed: {
    isLoggedIn() {
      return !!this.token;
    },
    // Use shared reactive conversations from chatState so new messages update list previews instantly
    conversations() {
      return chatState.conversations;
    },
    selectedId() {
      return this.$route.params.conversationId || this.selectedIdLocal;
    },
    selectedConversation() {
      const id = this.selectedId != null ? String(this.selectedId) : "";
      if (!id) return null;
      
      // First try active conversations
      const fromActive = this.conversations.find(
        (c) => String(c.talkjs_conversation_id || c.id) === id
      );
      if (fromActive) return fromActive;
      
      // Then try allChats (includes deleted conversations)
      const fromAll = this.allChats.find(
        (c) => String(c.talkjs_conversation_id || c.id) === id
      );
      if (fromAll) return fromAll;
      
      // Finally try cachedConversation (loaded via API for deleted chats)
      return this.cachedConversation || null;
    },
    // Panel actions for the admin action modal (group owner only)
    // Flutter sync: ChatModerationSheet - 3 actions only: Lock, Kick, Add
    panelActions() {
      const conv = this.selectedConversation;
      if (!conv || !this.panelIsGroupChat || !this.panelIsOwner) return [];

      const actions = [];

      // 1. Lock/Unlock Chat
      actions.push({
        id: 'lock',
        icon: this.panelIsLocked ? 'bi bi-unlock' : 'bi bi-lock',
        iconClass: this.panelIsLocked ? 'icon-success' : 'icon-warning',
        title: this.panelIsLocked
          ? this.$t('chat.unlock_chat') || 'Unlock Chat'
          : this.$t('chat.lock_chat') || 'Lock Chat',
        description: this.panelIsLocked
          ? this.$t('chat.unlock_description') || 'Allow all members to send messages'
          : this.$t('chat.lock_description') || 'Only allow admins to send messages',
      });

      // 2. Kick Student
      actions.push({
        id: 'kick',
        icon: 'bi bi-person-dash',
        iconClass: 'icon-danger',
        title: this.$t('chat.kick_student') || 'Kick Student',
        description: this.$t('chat.kick_student_description') || 'Remove a student from this group',
        dangerous: true,
      });

      // 3. Add Student
      actions.push({
        id: 'add',
        icon: 'bi bi-person-plus',
        iconClass: 'icon-primary',
        title: this.$t('chat.add_student') || 'Add Student',
        description: this.$t('chat.add_student_description') || 'Add a student to this group',
      });

      return actions;
    },
    // Members that can be kicked (exclude current user and admins)
    kickableMembers() {
      return this.membersList.filter(m => !m.is_current_user && !m.is_admin);
    },
    // Flutter sync: ChatInboxProvider._applyFilter logic
    filteredConversations() {
      let filtered = this.conversations;

      switch (this.conversationFilter) {
        case "unread":
          filtered = filtered.filter((c) => this.isConversationUnread(c));
          break;
        case "groups":
          filtered = filtered.filter((c) => {
            const type = c.custom?.type || "";
            return (
              type === "group" ||
              type === "student_group" ||
              type === "course_group"
            );
          });
          break;
        case "direct":
          filtered = filtered.filter((c) => {
            const type = c.custom?.type || "";
            return type === "direct";
          });
          break;
        case "all":
        default:
          break;
      }

      return filtered;
    },
    todayStr() {
      return new Date().toISOString().split("T")[0];
    },
    getMembersTypeLabel() {
      const type = this.membersMetadata.type;
      if (type === "direct") return this.$t("chat.direct_message");
      if (type === "student_group") return this.$t("chat.student_group");
      if (type === "group") {
        if (this.membersMetadata.course_id) return this.$t("chat.course_group");
        return this.$t("chat.group");
      }
      return this.$t("chat.conversation");
    },
    getConversationTypeIcon() {
      const type = this.membersMetadata.type;
      if (type === "direct") return "bi bi-chat-dots-fill";
      if (type === "student_group") return "bi bi-mortarboard-fill";
      if (type === "group") {
        if (this.membersMetadata.course_id) return "bi bi-book-fill";
        return "bi bi-people-fill";
      }
      return "bi bi-chat-fill";
    },
    panelDropdownStyle() {
      const isRtl =
        document.documentElement.dir === "rtl" || document.body.dir === "rtl";
      const style = {
        position: "fixed",
        top: this.panelDropdownPos.top + "px",
        zIndex: 99999,
      };
      if (isRtl) {
        // In RTL, position from left edge
        style.left = this.panelDropdownPos.left + "px";
      } else {
        // In LTR, position from right edge
        style.right = this.panelDropdownPos.right + "px";
      }
      return style;
    },
  },
  watch: {
    "$route.params.conversationId"() {
      /* reactive selected */
    },
    selectedId(newId) {
      // Load panel header meta when conversation changes
      this.showPanelMenu = false;
      this.showPanelMemberMenu = false;
      this.panelMetaLoaded = false; // Reset guard until new data is loaded
      if (newId) {
        this.loadPanelConversationMeta(newId);
      } else {
        this.panelIsGroupChat = false;
        this.panelIsBlocked = false;
        this.panelIsUserBlocker = false;
        this.panelIsOwner = false;
        this.panelIsLocked = false;
      }
    },
    showPanelSummaryDialog(val) {
      if (val) {
        this.panelSummaryPreset = "today";
        this.panelSummaryStartDate = this.todayStr;
        this.panelSummaryEndDate = this.todayStr;
        this.panelSummaryResult = null;
        this.panelSummaryError = null;
        this.panelSummaryGenerated = false;
      }
    },
    // Watch for when conversations list loads (handles initial page load timing)
    selectedConversation(newConv) {
      if (newConv && this.selectedId && !this.panelMetaLoaded) {
        this.loadPanelConversationMeta(this.selectedId);
      }
    },
    // Watch selectedId to load conversation from API if not found in lists
    async selectedId(newId) {
      if (!newId) {
        this.cachedConversation = null;
        return;
      }
      // Check if conversation exists in active or allChats
      const inActive = this.conversations.find(
        (c) => String(c.talkjs_conversation_id || c.id) === String(newId)
      );
      const inAll = this.allChats.find(
        (c) => String(c.talkjs_conversation_id || c.id) === String(newId)
      );
      
      if (!inActive && !inAll) {
        // Load from API (includes deleted conversations)
        try {
          const conv = await ChatService.getConversation(newId);
          if (conv) {
            this.cachedConversation = conv;
          }
        } catch (e) {
          console.error('[ChatMaster] Failed to load conversation:', e);
        }
      } else {
        this.cachedConversation = null;
      }
    },
  },
  mounted() {
    if (this.isLoggedIn) {
      this.fetchConversations().then(() => {
        // Subscribe to Pusher for all conversations after loading
        this.subscribeToAllConversations();
        // Also load allChats for deleted conversation lookup
        this.loadAllChats();
      });
      this.fetchPendingRequestsCount();
    }
    // Theme detection
    this.updateTheme();
    this.themeObserver = new MutationObserver(() => this.updateTheme());
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme", "data-theme"],
    });
  },
  beforeUnmount() {
    // Cleanup Pusher subscriptions and polling
    this.unsubscribeFromAllConversations();
    // Cleanup theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    updateTheme() {
      const theme =
        document.documentElement.getAttribute("data-bs-theme") ||
        document.documentElement.getAttribute("data-theme") ||
        "light";
      const isDark = theme === "dark";
      const chatMaster = this.$el?.querySelector(".chat-master");
      if (chatMaster) {
        if (isDark) {
          chatMaster.classList.add("chat-master-dark");
        } else {
          chatMaster.classList.remove("chat-master-dark");
        }
      }
    },
    // ── Real-Time Updates: Pusher + Polling Fallback ──────────────────────────
    async subscribeToAllConversations() {
      let pusherWorking = false;
      
      try {
        await pusherService.initialize();
        if (pusherService.isInitialized) {
          // Subscribe to each conversation channel
          this.conversations.forEach((conv) => {
            const convId = conv.talkjs_conversation_id || conv.id;
            if (!convId) return;

            pusherService.subscribeConversation(convId);
            const unsubscribe = pusherService.onConversationEvent(convId, (event) => {
              this.handleConversationEvent(convId, event);
            });
            this.pusherSubscriptions.push({ convId, unsubscribe });
          });

          console.log("[ChatMaster] Subscribed to", this.pusherSubscriptions.length, "conversations via Pusher");
          pusherWorking = true;
        }
      } catch (e) {
        console.error("[ChatMaster] Pusher subscription failed:", e);
      }

      // Always start polling as fallback (every 10 seconds)
      // This ensures updates even if Pusher fails or is not configured
      this.startPolling();
      console.log("[ChatMaster] Polling started as fallback");
    },
    unsubscribeFromAllConversations() {
      // Stop polling
      this.stopPolling();
      
      // Unsubscribe from Pusher
      this.pusherSubscriptions.forEach(({ convId, unsubscribe }) => {
        if (unsubscribe) unsubscribe();
        pusherService.unsubscribeConversation(convId);
      });
      this.pusherSubscriptions = [];
    },
    startPolling() {
      // Clear any existing interval
      this.stopPolling();
      
      // Poll every 10 seconds for new messages
      this.pollingInterval = setInterval(async () => {
        try {
          const list = await ChatService.getConversations();
          const conversations = Array.isArray(list) ? list : [];
          
          // Update conversations in shared state
          // This will trigger reactivity and update the UI
          chatState.conversations.splice(0, chatState.conversations.length, ...conversations);
          chatState.lastConversationUpdate = Date.now();
        } catch (e) {
          console.error("[ChatMaster] Polling error:", e);
        }
      }, 10000); // 10 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async handleConversationEvent(conversationId, event) {
      const eventName = event.event;
      const data = event.data || {};

      if (eventName === "message.sent") {
        // New message received - update preview
        const messageId = data.message_id?.toString();
        const senderId = data.sender_id?.toString();
        
        // Skip if it's our own message (already handled by Conversation.vue)
        if (senderId === String(chatState.userId)) return;
        
        if (messageId) {
          // Fetch full message to get text content
          const message = await ChatService.getMessage({
            conversationId,
            messageId,
          });
          
          if (message) {
            updateLastMessage(conversationId, message);
            bumpConversationToTop(conversationId);
            
            // Also increment unread count if not currently viewing this conversation
            if (this.selectedId !== conversationId) {
              const idx = chatState.conversations.findIndex(
                (c) => (c.talkjs_conversation_id || c.id) === conversationId
              );
              if (idx !== -1) {
                const conv = chatState.conversations[idx];
                chatState.conversations.splice(idx, 1, {
                  ...conv,
                  unreadMessageCount: (conv.unreadMessageCount || 0) + 1,
                  isUnread: true,
                });
              }
            }
          }
        }
      }
    },
    async fetchConversations() {
      this.loading = true;
      this.error = null;
      try {
        const list = await ChatService.getConversations();
        const conversations = Array.isArray(list) ? list : [];
        // Populate shared reactive store for real-time preview updates
        chatState.conversations.splice(0, chatState.conversations.length, ...conversations);
        // Build map for message search result lookups
        this.msgSearchConversationsMap = {};
        conversations.forEach((c) => {
          const id = c.talkjs_conversation_id || c.id;
          if (id) this.msgSearchConversationsMap[String(id)] = c;
        });
      } catch (_) {
        this.error = this.$t("chat.failed_to_load") || "Failed to load chat";
      } finally {
        this.loading = false;
      }
    },
    // ── All Chats Modal (Flutter sync: _showAllConversations) ─────────────────
    async openAllChatsModal() {
      this.showAllChatsModal = true;
      this.allChatsSearch = '';
      await this.loadAllChats();
    },
    closeAllChatsModal() {
      this.showAllChatsModal = false;
      this.allChats = [];
      this.allChatsFiltered = [];
      this.allChatsError = null;
      this.allChatsSearch = '';
      if (this.allChatsSearchDebounce) clearTimeout(this.allChatsSearchDebounce);
    },
    async loadAllChats() {
      this.allChatsLoading = true;
      this.allChatsError = null;
      try {
        const list = await ChatService.getAllConversations();
        this.allChats = list;
        this.allChatsFiltered = list;
      } catch (e) {
        this.allChatsError = this.$t('chat.failed_to_load') || 'Failed to load';
      } finally {
        this.allChatsLoading = false;
        this.allChatsSearchLoading = false;
      }
    },
    onAllChatsSearch() {
      if (this.allChatsSearchDebounce) clearTimeout(this.allChatsSearchDebounce);
      const q = this.allChatsSearch.trim().toLowerCase();
      if (!q) {
        this.allChatsFiltered = this.allChats;
        this.allChatsSearchLoading = false;
        return;
      }
      this.allChatsSearchLoading = true;
      this.allChatsSearchDebounce = setTimeout(() => {
        // Local filtering for instant search
        this.allChatsFiltered = this.allChats.filter(c => {
          const name = this.displayNameForAllChats(c).toLowerCase();
          const subject = (c.subject || '').toLowerCase();
          return name.includes(q) || subject.includes(q);
        });
        this.allChatsSearchLoading = false;
      }, 200);
    },
    openAllChatsConversation(c) {
      // Cache the conversation before closing modal (prevents name disappearing)
      if (c) {
        this.cachedConversation = c;
        upsertConversation(c);
      }
      this.closeAllChatsModal();
      this.selectConversation(c);
    },
    // ─────────────────────────────────────────────────────────────────────────
    // ── Message content search ────────────────────────────────────────────────
    // ─────────────────────────────────────────────────────────────────────────
    // Open advanced search page
    openSearchPage() {
      this.$router.push({ name: 'ChatSearch' }).catch(() => {});
    },
    // ─────────────────────────────────────────────────────────────────────────
    selectConversation(c) {
      const id = c.talkjs_conversation_id || c.id;
      if (!id) return;
      // Optimistically mark as read immediately
      this.markConversationAsReadLocally(id);
      // Keep route param so link is shareable
      this.selectedIdLocal = id;
      this.$router
        .push({
          name: "ChatConversation",
          params: { conversationId: String(id) },
        })
        .catch(() => {});
    },
    // Check if conversation has unread messages
    isConversationUnread(c) {
      return (
        c.isUnread === true ||
        c.is_unread === true ||
        this.getUnreadCount(c) > 0
      );
    },
    // Check if conversation is muted (from conversation object fields)
    isConversationMuted(c) {
      return (
        c.is_muted === true ||
        c.isMuted === true ||
        c.is_muted_permanently === true ||
        c.isMutedPermanently === true ||
        (c.muted_until && new Date(c.muted_until) > new Date())
      );
    },
    // Get unread message count for a conversation
    getUnreadCount(c) {
      return c.unreadMessageCount || c.unread_count || 0;
    },
    // Optimistically mark conversation as read in shared state
    markConversationAsReadLocally(conversationId) {
      const idx = chatState.conversations.findIndex(
        (c) => (c.talkjs_conversation_id || c.id) === conversationId
      );
      if (idx !== -1) {
        // Create updated conversation with unread cleared
        const updated = {
          ...chatState.conversations[idx],
          unreadMessageCount: 0,
          unread_count: 0,
          isUnread: false,
          is_unread: false,
        };
        chatState.conversations.splice(idx, 1, updated);
        chatState.lastConversationUpdate = Date.now();
      }
    },
    // Get the other participant's info for direct chats
    getDirectChatPeer(c) {
      const custom = c.custom || {};
      const currentUserId = String(this.getCurrentUserId());
      
      // Try participants_details from custom
      let details = [];
      try {
        const raw = custom.participants_details;
        if (typeof raw === "string" && raw.trim().length) {
          details = JSON.parse(raw);
        } else if (Array.isArray(raw)) {
          details = raw;
        }
      } catch (_) {}

      if (Array.isArray(details) && details.length) {
        const other = details.find((p) => p && String(p.id) !== currentUserId);
        if (other) return other;
        if (details[0]) return details[0];
      }
      
      // Try other_user field (common in deleted chats)
      if (c.other_user) return c.other_user;
      if (c.otherUser) return c.otherUser;
      
      // Try participants array
      if (c.participants && Array.isArray(c.participants)) {
        const other = c.participants.find(p => String(p.id) !== currentUserId);
        if (other) return other;
        if (c.participants[0]) return c.participants[0];
      }
      
      // Try peer field
      if (c.peer) return c.peer;
      
      return null;
    },
    getCurrentUserId() {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          return payload.sub || payload.user_id || payload.id || "";
        }
      } catch (_) {}
      return "";
    },
    displayName(c) {
      // For direct chats, show the other participant's name
      const peer = this.getDirectChatPeer(c);
      if (peer && peer.name) return peer.name;

      // Try subject
      if (c.subject) return c.subject;
      
      // Try name field directly (some APIs return this)
      if (c.name) return c.name;
      
      // Try title field
      if (c.title) return c.title;

      // Try participantNames
      if (c.participantNames) {
        const names = Object.values(c.participantNames);
        if (names && names.length) {
          // Filter out current user's name if possible
          const currentUserId = String(this.getCurrentUserId());
          const otherNames = names.filter((_, idx) => {
            const keys = Object.keys(c.participantNames);
            return keys[idx] !== currentUserId;
          });
          if (otherNames.length) return otherNames[0];
          return names[0];
        }
      }
      
      // Try participants array (alternative format)
      if (c.participants && Array.isArray(c.participants)) {
        const currentUserId = String(this.getCurrentUserId());
        const other = c.participants.find(p => String(p.id) !== currentUserId);
        if (other && other.name) return other.name;
        if (c.participants[0] && c.participants[0].name) return c.participants[0].name;
      }
      
      // Try custom.name or custom.title
      const custom = c.custom || {};
      if (custom.name) return custom.name;
      if (custom.title) return custom.title;
      
      // Try other_user field (for deleted chats)
      if (c.other_user && c.other_user.name) return c.other_user.name;
      if (c.otherUser && c.otherUser.name) return c.otherUser.name;
      
      return this.$t("common.chat") || "Chat";
    },
    // Enhanced display name for All Chats modal (handles deleted chats better)
    displayNameForAllChats(c) {
      // Try peer info first (for direct chats)
      const peer = this.getDirectChatPeer(c);
      if (peer && peer.name) return peer.name;

      // Try subject
      if (c.subject) return c.subject;
      
      // Try name field directly (some APIs return this)
      if (c.name) return c.name;
      
      // Try title field
      if (c.title) return c.title;

      // Try participantNames
      if (c.participantNames) {
        const names = Object.values(c.participantNames);
        if (names && names.length) {
          // Filter out current user's name if possible
          const currentUserId = String(this.getCurrentUserId());
          const otherNames = names.filter((_, idx) => {
            const keys = Object.keys(c.participantNames);
            return keys[idx] !== currentUserId;
          });
          if (otherNames.length) return otherNames[0];
          return names[0];
        }
      }
      
      // Try participants array (alternative format)
      if (c.participants && Array.isArray(c.participants)) {
        const currentUserId = String(this.getCurrentUserId());
        const other = c.participants.find(p => String(p.id) !== currentUserId);
        if (other && other.name) return other.name;
        if (c.participants[0] && c.participants[0].name) return c.participants[0].name;
      }
      
      // Try custom.name or custom.title
      const custom = c.custom || {};
      if (custom.name) return custom.name;
      if (custom.title) return custom.title;
      
      // Last resort: use conversation ID or generic name
      if (c.id) return `${this.$t("common.chat") || "Chat"} #${String(c.id).slice(-6)}`;
      return this.$t("common.chat") || "Chat";
    },
    // Get avatar URL for conversation (direct chat peer or conversation photo)
    getConversationAvatar(c) {
      const peer = this.getDirectChatPeer(c);
      if (peer && peer.image) return peer.image;
      return c.photoUrl || c.photo_url || "/assets/img/acad-icon.png";
    },
    lastMessagePreview(c) {
      const lm = c.lastMessage || c.last_message;
      if (!lm) return "";
      if (lm.text && lm.text.length > 0) return lm.text;
      const content = lm.content;
      if (
        Array.isArray(content) &&
        content.length &&
        content[0].type === "file"
      ) {
        const subtype = content[0].subtype;
        if (subtype === "image") return this.$t("chat.msg_photo") || "Photo";
        if (subtype === "video") return this.$t("chat.msg_video") || "Video";
        if (subtype === "voice") return this.$t("chat.msg_voice") || "Voice";
        return this.$t("chat.msg_document") || "Attachment";
      }
      return this.$t("common.message") || "Message";
    },
    onImgError(e) {
      e.target.src = "/assets/img/acad-icon.png";
    },
    async fetchPendingRequestsCount() {
      try {
        this.pendingRequestsCount = await ChatService.getPendingRequestsCount();
      } catch (e) {
        console.error(
          "[ChatMaster] Failed to fetch pending requests count:",
          e
        );
      }
    },
    goToMessageRequests() {
      this.$router.push({ name: "MessageRequests" });
    },
    goToCreateGroup() {
      this.$router.push({ name: "CreateGroupChat" });
    },
    onRequestSent() {
      // Refresh the pending count
      this.fetchPendingRequestsCount();
      // Redirect to Sent Requests page (matching mobile app flow)
      this.$router.push({ name: "MessageRequests", query: { tab: "sent" } });
    },
    toggleActionsMenu() {
      this.showActionsMenu = !this.showActionsMenu;
      if (this.showActionsMenu) {
        // Position dropdown below trigger
        this.$nextTick(() => {
          const trigger = this.$refs.menuTrigger;
          const dropdown = this.$refs.menuDropdown;
          if (trigger && dropdown) {
            const rect = trigger.getBoundingClientRect();
            dropdown.style.top = `${rect.bottom + 8}px`;
            dropdown.style.right = `${window.innerWidth - rect.right}px`;
          }
        });
        // Close menu when clicking outside
        setTimeout(() => {
          document.addEventListener("click", this.closeMenuOnClickOutside);
        }, 10);
      }
    },
    closeMenuOnClickOutside(e) {
      const trigger = this.$refs.menuTrigger;
      const dropdown = this.$refs.menuDropdown;
      if (
        trigger &&
        !trigger.contains(e.target) &&
        dropdown &&
        !dropdown.contains(e.target)
      ) {
        this.showActionsMenu = false;
        document.removeEventListener("click", this.closeMenuOnClickOutside);
      }
    },
    handleMenuAction(action) {
      this.showActionsMenu = false;
      document.removeEventListener("click", this.closeMenuOnClickOutside);
      switch (action) {
        case "qr":
          this.showQrCodeDialog = true;
          break;
        case "requests":
          this.goToMessageRequests();
          break;
        case "addFriend":
          this.showAddFriendDialog = true;
          break;
        case "createGroup":
          this.goToCreateGroup();
          break;
        case "friendsList":
          this.$router.push({ name: "ChatFriendsList" });
          break;
        case "blockedUsers":
          this.$router.push({ name: "ChatBlockedUsers" });
          break;
        case "allChats":
          this.openAllChatsModal();
          break;
      }
    },
    // ==================== Panel Header Conversation Meta ====================
    loadPanelConversationMeta(conversationId) {
      // Use selectedConversation from the already-fetched list (same data source as filters)
      const c = this.selectedConversation;
      if (!c) {
        // Reset to defaults if no conversation found - keep panelMetaLoaded false
        this.panelMetaLoaded = false;
        this.panelIsGroupChat = false;
        this.panelIsBlocked = false;
        this.panelIsUserBlocker = false;
        this.panelIsOwner = false;
        this.panelIsLocked = false;
        return;
      }

      const custom = c.custom || {};
      const currentUserId = this.getCurrentUserId();

      // Use same logic as filteredConversations for consistency
      const type = custom.type || "";
      this.panelIsGroupChat =
        type === "group" || type === "student_group" || type === "course_group";

      let isEnabled = true;
      if (custom.chat_enabled === false) isEnabled = false;
      if (typeof custom.chat_enabled === "string")
        isEnabled = custom.chat_enabled.toLowerCase() === "true";
      this.panelIsLocked = !isEnabled;

      const blockedBy = custom.blocked_by;
      this.panelIsBlocked = !!blockedBy;
      this.panelIsUserBlocker =
        !!blockedBy && String(blockedBy) === String(currentUserId);

      const creatorId =
        custom.creator_id != null ? String(custom.creator_id) : null;
      this.panelIsOwner = !!(creatorId && creatorId === String(currentUserId));

      // Set mute status from conversation object (instant, no API call)
      this.panelIsMuted = this.isConversationMuted(c);

      // Mark metadata as loaded - now safe to render actions
      this.panelMetaLoaded = true;
    },
    // ==================== Panel Actions Menu ====================
    togglePanelMenu() {
      this.showPanelMenu = !this.showPanelMenu;
      if (this.showPanelMenu) {
        // Calculate position from the trigger button ref
        const btn = this.$refs.panelMenuBtn;
        if (btn) {
          const rect = btn.getBoundingClientRect();
          const isRtl =
            document.documentElement.dir === "rtl" ||
            document.body.dir === "rtl";
          this.panelDropdownPos = {
            top: rect.bottom + 6,
            right: window.innerWidth - rect.right,
            left: 16, // For RTL (Arabic)
          };
        }
        setTimeout(() => {
          document.addEventListener("click", this._closePanelMenuOutside);
        }, 10);
      }
    },
    _closePanelMenuOutside(e) {
      // The dropdown is teleported to body, so check both the trigger btn and the dropdown itself
      const btn = this.$refs.panelMenuBtn;
      const dropdown = document.querySelector(".chat-panel-dropdown");
      if (btn && btn.contains(e.target)) return;
      if (dropdown && dropdown.contains(e.target)) return;
      this.showPanelMenu = false;
      this.showPanelMemberMenu = false;
      document.removeEventListener("click", this._closePanelMenuOutside);
    },
    // ==================== Panel Block/Unblock ====================
    async panelBlockUser() {
      this.showPanelMenu = false;
      this.showPanelMemberMenu = false;
      if (this.panelIsGroupChat) return;
      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.block_user") || "Block User",
        text:
          this.$t("chat.block_user_confirmation") ||
          "Are you sure you want to block this user?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) return;
      try {
        const res = await ChatService.blockUser(this.selectedId);
        if (res.success) {
          this.panelIsBlocked = true;
          this.panelIsUserBlocker = true;
          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.user_blocked") || "User has been blocked",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title:
              res.message ||
              this.$t("chat.failed_to_block") ||
              "Failed to block user",
          });
        }
      } catch (e) {
        console.error("[ChatMaster] Block user error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.failed_to_block") || "Failed to block user",
        });
      }
    },
    async panelUnblockUser() {
      this.showPanelMenu = false;
      this.showPanelMemberMenu = false;
      if (this.panelIsGroupChat || !this.panelIsUserBlocker) return;
      const result = await Swal.fire({
        ...getSwalConfig("question"),
        title: this.$t("chat.unblock_user") || "Unblock User",
        text:
          this.$t("chat.unblock_user_confirmation") ||
          "Are you sure you want to unblock this user?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#00c444",
      });
      if (!result.isConfirmed) return;
      try {
        const res = await ChatService.unblockUser(this.selectedId);
        if (res.success) {
          this.panelIsBlocked = false;
          this.panelIsUserBlocker = false;
          Swal.fire({
            ...getSwalConfig("success"),
            title: this.$t("chat.user_unblocked") || "User has been unblocked",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig("error"),
            title:
              res.message ||
              this.$t("chat.failed_to_unblock") ||
              "Failed to unblock user",
          });
        }
      } catch (e) {
        console.error("[ChatMaster] Unblock user error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.failed_to_unblock") || "Failed to unblock user",
        });
      }
    },
    // ==================== Panel Group Moderation ====================
    // Handle action from ChatActionModal (Admin actions only)
    // Flutter sync: ChatModerationSheet actions
    handlePanelAction(actionId) {
      this.showActionModal = false;
      switch (actionId) {
        case 'lock':
          this.panelToggleLock();
          break;
        case 'kick':
          this.showKickStudentModal();
          break;
        case 'add':
          this.showAddStudentModal();
          break;
      }
    },
    // Show modal to select student to kick
    async showKickStudentModal() {
      if (!this.panelIsOwner || !this.panelIsGroupChat) return;
      
      // Load members first
      this.membersLoading = true;
      this.membersError = null;
      this.membersList = [];
      
      try {
        const result = await ChatService.getConversationMembers(this.selectedId);
        if (result.success) {
          this.membersList = result.data.members || [];
        }
      } catch (e) {
        console.error('[ChatMaster] Failed to load members:', e);
      } finally {
        this.membersLoading = false;
      }
      
      if (this.kickableMembers.length === 0) {
        Swal.fire({
          ...getSwalConfig('info'),
          title: this.$t('chat.no_other_participants') || 'No Other Participants',
          text: this.$t('chat.no_students_to_kick') || 'There are no students to kick from this group.',
        });
        return;
      }
      
      this.membersModalMode = 'kick';
      this.showMembersModal = true;
    },
    // Show modal to select student to add
    async showAddStudentModal() {
      if (!this.panelIsOwner || !this.panelIsGroupChat) return;
      
      this.membersLoading = true;
      this.membersError = null;
      this.membersList = [];
      this.friendsListForAdd = [];
      
      try {
        // Load current members first
        const membersResult = await ChatService.getConversationMembers(this.selectedId);
        if (membersResult.success) {
          this.membersList = membersResult.data.members || [];
        }
        
        // Load friends list
        const friends = await ChatService.getFriendsList();
        
        // Filter out users already in the group
        const currentMemberIds = new Set(this.membersList.map(m => String(m.id)));
        this.friendsListForAdd = friends.filter(f => !currentMemberIds.has(String(f.id)));
      } catch (e) {
        console.error('[ChatMaster] Failed to load friends:', e);
        this.friendsListForAdd = [];
      } finally {
        this.membersLoading = false;
      }
      
      if (this.friendsListForAdd.length === 0) {
        Swal.fire({
          ...getSwalConfig('info'),
          title: this.$t('chat.no_friends_to_add') || 'No Friends to Add',
          text: this.$t('chat.no_friends_to_add_desc') || 'All your friends are already in this group or you have no friends to add.',
        });
        return;
      }
      
      this.membersModalMode = 'add';
      this.showMembersModal = true;
    },
    // Kick selected student from group
    async kickStudent(student) {
      const confirmed = await Swal.fire({
        ...getSwalConfig('warning'),
        title: this.$t('chat.kick_student') || 'Kick Student',
        text: (this.$t('chat.kick_student_confirmation') || 'Are you sure you want to kick {name} from this group?').replace('{name}', student.name),
        showCancelButton: true,
        confirmButtonText: this.$t('chat.kick') || 'Kick',
        cancelButtonText: this.$t('common.cancel') || 'Cancel',
        confirmButtonColor: '#ef4444',
      });
      
      if (!confirmed.isConfirmed) return;
      
      try {
        this.membersLoading = true;
        const result = await ChatService.kickUser(this.selectedId, student.id);
        
        if (result.success) {
          Swal.fire({
            ...getSwalConfig('success'),
            title: this.$t('chat.student_kicked') || 'Student Kicked',
            text: result.message || (this.$t('chat.student_kicked_success') || 'Student has been removed from the group.'),
            timer: 2000,
            showConfirmButton: false,
          });
          this.closeMembersModal();
          // Refresh conversation data
          this.loadPanelConversationMeta(this.selectedId);
        } else {
          Swal.fire({
            ...getSwalConfig('error'),
            title: this.$t('chat.error') || 'Error',
            text: result.message || this.$t('chat.failed_to_kick') || 'Failed to kick student',
          });
        }
      } catch (e) {
        console.error('[ChatMaster] Kick student error:', e);
        Swal.fire({
          ...getSwalConfig('error'),
          title: this.$t('chat.error') || 'Error',
          text: this.$t('chat.failed_to_kick') || 'Failed to kick student',
        });
      } finally {
        this.membersLoading = false;
      }
    },
    // Add selected friend to group
    async addStudent(friend) {
      const confirmed = await Swal.fire({
        ...getSwalConfig('question'),
        title: this.$t('chat.add_student') || 'Add Student',
        text: (this.$t('chat.add_student_confirmation') || 'Are you sure you want to add {name} to this group?').replace('{name}', friend.name),
        showCancelButton: true,
        confirmButtonText: this.$t('common.add') || 'Add',
        cancelButtonText: this.$t('common.cancel') || 'Cancel',
      });
      
      if (!confirmed.isConfirmed) return;
      
      try {
        this.membersLoading = true;
        const result = await ChatService.addUserToGroup(this.selectedId, friend.id);
        
        if (result.success) {
          Swal.fire({
            ...getSwalConfig('success'),
            title: this.$t('chat.student_added') || 'Student Added',
            text: result.message || (this.$t('chat.student_added_success') || 'Student has been added to the group.'),
            timer: 2000,
            showConfirmButton: false,
          });
          this.closeMembersModal();
          // Refresh conversation data
          this.loadPanelConversationMeta(this.selectedId);
        } else {
          Swal.fire({
            ...getSwalConfig('error'),
            title: this.$t('chat.error') || 'Error',
            text: result.message || this.$t('chat.failed_to_add') || 'Failed to add student',
          });
        }
      } catch (e) {
        console.error('[ChatMaster] Add student error:', e);
        Swal.fire({
          ...getSwalConfig('error'),
          title: this.$t('chat.error') || 'Error',
          text: this.$t('chat.failed_to_add') || 'Failed to add student',
        });
      } finally {
        this.membersLoading = false;
      }
    },
    async panelToggleLock() {
      this.showPanelMenu = false;
      if (!this.panelIsOwner) return;
      
      // Determine action text based on current state
      const isLocking = !this.panelIsLocked;
      const actionTitle = isLocking 
        ? (this.$t('chat.lock_chat') || 'Lock Chat')
        : (this.$t('chat.unlock_chat') || 'Unlock Chat');
      const actionText = isLocking
        ? (this.$t('chat.lock_chat_confirm') || 'Members will not be able to send messages')
        : (this.$t('chat.unlock_chat_confirm') || 'Members will be able to send messages again');
      
      // Confirmation dialog
      const confirmed = await Swal.fire({
        ...getSwalConfig(isLocking ? 'warning' : 'question'),
        title: actionTitle,
        text: actionText,
        showCancelButton: true,
        confirmButtonText: this.$t('common.confirm') || 'Confirm',
        cancelButtonText: this.$t('common.cancel') || 'Cancel',
      });
      
      if (!confirmed.isConfirmed) return;
      
      // Show loading
      Swal.fire({
        ...getSwalConfig('info'),
        title: this.$t('common.please_wait') || 'Please wait...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      
      try {
        // Pass enabled: true to unlock, false to lock (opposite of current state)
        const newEnabled = this.panelIsLocked; // If locked, we want to enable (unlock)
        const result = await ChatService.toggleChatLock(this.selectedId, newEnabled);
        if (result.success) {
          this.panelIsLocked = !this.panelIsLocked;
          // Show success message
          const successMessage = this.panelIsLocked 
            ? (this.$t('chat.chat_locked') || 'Chat locked')
            : (this.$t('chat.chat_unlocked') || 'Chat unlocked');
          Swal.fire({
            ...getSwalConfig('success'),
            title: successMessage,
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            ...getSwalConfig('error'),
            title: this.$t('chat.error') || 'Error',
            text: result.message || this.$t('chat.failed_to_toggle_lock') || 'Failed to toggle chat lock',
          });
        }
      } catch (e) {
        console.error("[ChatMaster] Toggle chat lock error:", e);
        Swal.fire({
          ...getSwalConfig('error'),
          title: this.$t('chat.error') || 'Error',
          text: this.$t('chat.failed_to_toggle_lock') || 'Failed to toggle chat lock',
        });
      }
    },
    async panelToggleMute() {
      this.showPanelMemberMenu = false;
      if (this.panelIsMuted) {
        // Unmute
        await this.panelUnmuteConversation();
      } else {
        // Open mute dialog
        this.$refs.conversationComponent?.openMuteDialog();
      }
    },
    async panelLeaveGroup() {
      this.showPanelMemberMenu = false;
      if (!this.panelIsGroupChat) return;
      
      const confirmed = window.confirm(
        this.$t('chat.leave_group_confirm') || 'Are you sure you want to leave this group?'
      );
      if (!confirmed) return;
      
      try {
        const result = await ChatService.leaveGroup(this.selectedId);
        if (result.success) {
          // Remove from shared conversations list
          const leaveIdx = chatState.conversations.findIndex(
            c => (c.talkjs_conversation_id || c.id) === this.selectedId
          );
          if (leaveIdx !== -1) {
            chatState.conversations.splice(leaveIdx, 1);
            chatState.lastConversationUpdate = Date.now();
          }
          // Navigate away
          this.selectedIdLocal = null;
          this.$router.push({ name: 'Chat' }).catch(() => {});
        } else {
          alert(result.message || this.$t('chat.failed_to_leave_group') || 'Failed to leave group');
        }
      } catch (e) {
        console.error('[ChatMaster] Leave group error:', e);
        alert(this.$t('chat.failed_to_leave_group') || 'Failed to leave group');
      }
    },
    async panelDeleteConversation() {
      this.showPanelMemberMenu = false;
      
      const confirmed = window.confirm(
        this.$t('chat.delete_chat_confirmation') || 'Are you sure you want to delete this chat?'
      );
      if (!confirmed) return;
      
      try {
        const result = await ChatService.deleteConversation(this.selectedId);
        if (result.success) {
          // Remove from shared conversations list
          const deleteIdx = chatState.conversations.findIndex(
            c => (c.talkjs_conversation_id || c.id) === this.selectedId
          );
          if (deleteIdx !== -1) {
            chatState.conversations.splice(deleteIdx, 1);
            chatState.lastConversationUpdate = Date.now();
          }
          // Navigate away
          this.selectedIdLocal = null;
          this.$router.push({ name: 'Chat' }).catch(() => {});
          // Show success message
          alert(this.$t('chat.chat_deleted_successfully') || 'Chat deleted successfully');
        } else {
          alert(result.message || this.$t('chat.failed_to_delete_chat') || 'Failed to delete chat');
        }
      } catch (e) {
        console.error('[ChatMaster] Delete conversation error:', e);
        alert(this.$t('chat.failed_to_delete_chat') || 'Failed to delete chat');
      }
    },
    panelOpenMuteDialog() {
      this.showPanelMenu = false;
      // Check current mute state from conversation object
      const isMuted = this.isConversationMuted(this.selectedConversation);

      if (isMuted) {
        // If muted, unmute immediately
        this.panelUnmuteConversation();
      } else {
        // If not muted, open mute dialog
        this.$refs.conversationComponent?.openMuteDialog();
      }
    },
    async panelUnmuteConversation() {
      if (!this.selectedId) return;
      try {
        await ChatService.unmuteConversation(this.selectedId);
        console.log('[ChatMaster] Conversation unmuted successfully');
        // Update local state immediately
        this.onConversationUnmuted();
      } catch (e) {
        console.error('[ChatMaster] Failed to unmute conversation:', e);
      }
    },
    // Handle mute event from Conversation component - update shared state instantly
    onConversationMuted(duration) {
      console.log('[ChatMaster] Conversation muted for:', duration);
      // Update panel mute status
      this.panelIsMuted = true;
      // Update conversation object in shared state
      const idx = chatState.conversations.findIndex(
        (c) => (c.talkjs_conversation_id || c.id) === this.selectedId
      );
      if (idx !== -1) {
        const isPermanent = duration === 'always' || duration === 'permanent';
        let mutedUntil = null;

        if (!isPermanent) {
          // Calculate muted_until based on duration
          const minutes = {
            '8h': 8 * 60,
            '1w': 7 * 24 * 60,
            '1d': 24 * 60,
            '1h': 60,
          }[duration] || 8 * 60; // Default to 8 hours
          mutedUntil = new Date(Date.now() + minutes * 60 * 1000).toISOString();
        }

        const updated = { ...chatState.conversations[idx] };
        updated.is_muted = true;
        updated.isMuted = true;
        updated.is_muted_permanently = isPermanent;
        updated.isMutedPermanently = isPermanent;
        updated.muted_until = mutedUntil;
        chatState.conversations.splice(idx, 1, updated);
        chatState.lastConversationUpdate = Date.now();
      }
    },
    // Handle unmute event from Conversation component - update shared state instantly
    onConversationUnmuted() {
      console.log('[ChatMaster] Conversation unmuted');
      // Update panel mute status
      this.panelIsMuted = false;
      // Update conversation object in shared state
      const idx = chatState.conversations.findIndex(
        (c) => (c.talkjs_conversation_id || c.id) === this.selectedId
      );
      if (idx !== -1) {
        const updated = { ...chatState.conversations[idx] };
        updated.is_muted = false;
        updated.isMuted = false;
        updated.is_muted_permanently = false;
        updated.isMutedPermanently = false;
        updated.muted_until = null;
        chatState.conversations.splice(idx, 1, updated);
        chatState.lastConversationUpdate = Date.now();
      }
    },
    async panelLeaveGroup() {
      this.showPanelMenu = false;
      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.leave_group") || "Leave Group",
        text:
          this.$t("chat.leave_group_confirmation") ||
          "Are you sure you want to leave this group?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.confirm") || "Confirm",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) return;
      try {
        await ChatService.leaveConversation(this.selectedId);
        Swal.fire({
          ...getSwalConfig("success"),
          title:
            this.$t("chat.left_group_successfully") ||
            "You have left the group",
          timer: 2000,
          showConfirmButton: false,
        });
        this.selectedIdLocal = null;
        this.$router.push({ name: "ChatInbox" }).catch(() => {});
        this.fetchConversations();
      } catch (e) {
        console.error("[ChatMaster] Leave group error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.error") || "Error",
          text: this.$t("chat.failed_to_leave_group") || "Failed to leave group",
        });
      }
    },
    async panelDeleteConversation() {
      this.showPanelMenu = false;
      const result = await Swal.fire({
        ...getSwalConfig("warning"),
        title: this.$t("chat.delete_chat") || "Delete Chat",
        text:
          this.$t("chat.delete_chat_confirmation") ||
          "Are you sure you want to delete this chat?",
        showCancelButton: true,
        confirmButtonText: this.$t("common.delete_") || "Delete",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        confirmButtonColor: "#ef4444",
      });
      if (!result.isConfirmed) return;
      try {
        await ChatService.deleteConversation(this.selectedId);
        Swal.fire({
          ...getSwalConfig("success"),
          title:
            this.$t("chat.chat_deleted_successfully") ||
            "Chat deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        this.selectedIdLocal = null;
        this.$router.push({ name: "ChatInbox" }).catch(() => {});
        this.fetchConversations();
      } catch (e) {
        console.error("[ChatMaster] Delete conversation error:", e);
        Swal.fire({
          ...getSwalConfig("error"),
          title: this.$t("chat.error") || "Error",
          text: this.$t("chat.failed_to_delete_chat") || "Failed to delete chat",
        });
      }
    },
    // ==================== Panel Mia Summary ====================
    openPanelSummary() {
      this.showPanelSummaryDialog = true;
    },
    toggleConversationSearch() {
      this.$refs.conversationComponent?.toggleSearchMode();
    },
    selectPanelSummaryPreset(preset) {
      this.panelSummaryPreset = preset;
      // Don't clear the summary result when switching tabs - only clear error
      this.panelSummaryError = null;
      const now = new Date();
      const todayStr = now.toISOString().split("T")[0];
      if (preset === "today") {
        this.panelSummaryStartDate = todayStr;
        this.panelSummaryEndDate = todayStr;
      } else if (preset === "yesterday") {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        const yStr = y.toISOString().split("T")[0];
        this.panelSummaryStartDate = yStr;
        this.panelSummaryEndDate = yStr;
      }
    },
    async panelGenerateSummary() {
      if (this.panelSummaryLoading || !this.selectedId) return;
      this.panelSummaryLoading = true;
      this.panelSummaryResult = null;
      this.panelSummaryError = null;
      this.panelSummaryMessageCount = null;
      this.panelSummaryMetadata = null;
      try {
        const todayStr = new Date().toISOString().split("T")[0];
        const startDate = new Date(this.panelSummaryStartDate || todayStr);
        let endDate;
        if (this.panelSummaryPreset === "yesterday") {
          endDate = new Date(this.panelSummaryEndDate || todayStr);
          endDate.setHours(23, 59, 59, 999);
        } else if (this.panelSummaryPreset === "today") {
          endDate = new Date();
        } else {
          endDate = new Date(this.panelSummaryEndDate || todayStr);
          endDate.setHours(23, 59, 59, 999);
        }
        const result = await ChatService.getChatSummary(
          this.selectedId,
          startDate,
          endDate
        );
        if (result.success && result.summary) {
          this.panelSummaryResult = result.summary;
          this.panelSummaryMessageCount = result.message_count;
          this.panelSummaryMetadata = result.metadata;
          this.panelSummaryGenerated = true;
        } else {
          this.panelSummaryError =
            result.message ||
            this.$t("chat.failed_to_load_summary") ||
            "Failed to generate summary";
        }
      } catch (e) {
        console.error("[ChatMaster] Summary error:", e);
        this.panelSummaryError =
          this.$t("chat.failed_to_load_summary") ||
          "Failed to generate summary";
      } finally {
        this.panelSummaryLoading = false;
      }
    },
    panelCopySummary() {
      if (this.panelSummaryResult) {
        navigator.clipboard
          .writeText(this.panelSummaryResult)
          .then(() => {
            alert(
              this.$t("chat.summary_copied") || "Summary copied to clipboard"
            );
          })
          .catch(() => {});
      }
    },
    panelShareSummary() {
      if (this.panelSummaryResult && navigator.share) {
        navigator
          .share({
            title:
              this.$t("chat.conversation_summary") || "Conversation Summary",
            text: this.panelSummaryResult,
          })
          .catch(() => {});
      } else if (this.panelSummaryResult) {
        this.panelCopySummary();
      }
    },
    // ==================== Members Modal ====================
    async openMembersModal() {
      if (!this.selectedId) return;
      this.showMembersModal = true;
      this.membersLoading = true;
      this.membersError = null;
      this.membersList = [];
      this.membersMetadata = {
        conversation_id: null,
        type: null,
        course_id: null,
        creator_id: null,
        admin_id: null,
        total_members: 0,
      };

      try {
        const result = await ChatService.getConversationMembers(
          this.selectedId
        );
        if (result.success) {
          this.membersList = result.data.members || [];
          this.membersMetadata = {
            conversation_id: result.data.conversation_id,
            type: result.data.type,
            course_id: result.data.course_id,
            creator_id: result.data.creator_id,
            admin_id: result.data.admin_id,
            total_members: result.data.total_members,
          };
        } else {
          // Handle specific error codes
          if (result.code === 403) {
            this.membersError = this.$t("chat.not_participant");
          } else if (result.code === 404) {
            this.membersError = this.$t("chat.conversation_not_found");
          } else if (result.code >= 500) {
            this.membersError = this.$t("chat.server_error");
          } else {
            this.membersError =
              result.error || this.$t("chat.failed_to_load_members");
          }
        }
      } catch (err) {
        console.error("[ChatMaster] Failed to fetch members:", err);
        this.membersError = this.$t("chat.failed_to_load_members");
      } finally {
        this.membersLoading = false;
      }
    },
    closeMembersModal() {
      this.showMembersModal = false;
      this.membersError = null;
      this.membersList = [];
      this.membersModalMode = 'view';
      this.friendsListForAdd = [];
    },
  },
  beforeUnmount() {
    if (this.themeObserver) this.themeObserver.disconnect();
    document.removeEventListener("click", this._closePanelMenuOutside);
    if (this.msgSearchDebounce) clearTimeout(this.msgSearchDebounce);
    if (this.allChatsSearchDebounce) clearTimeout(this.allChatsSearchDebounce);
  },
};
</script>

<style scoped>
/* ========================================
   Chat Master Layout - Fullwidth Attached
   ======================================== */

/* Fullwidth page wrapper - attached to sidebar and header */
.chat-page-fullwidth {
  position: fixed;
  top: 65px; /* Below header */
  left: var(
    --bs-app-sidebar-width,
    265px
  ); /* After sidebar - uses CSS variable */
  right: 0;
  bottom: 0;
  padding: 0;
  background: #ffffff;
  overflow: hidden;
  transition: left 0.3s ease, right 0.3s ease;
}

/* RTL Support */
[dir="rtl"] .chat-page-fullwidth,
html[lang="ar"] .chat-page-fullwidth {
  left: 0;
  right: var(--bs-app-sidebar-width, 265px);
}

/* Login card centered */
.chat-login-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: white;
  border-radius: 0;
  padding: 40px;
  text-align: center;
}

/* Main container - fills available space */
.chat-master {
  display: grid;
  grid-template-columns: 320px 1fr;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background: #ffffff;
  box-shadow: none;
}

/* ========== Filter Tabs (Flutter sync) ========== */
.chat-filter-tabs {
  display: flex;
  padding: 6px 10px;
  gap: 4px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.chat-filter-tab {
  flex: 0 1 auto;
  padding: 5px 10px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.chat-filter-tab:hover {
  background: #e5e7eb;
  color: #374151;
}

.chat-filter-tab.active {
  background: #00c444;
  color: white;
  font-weight: 600;
}

/* ========== Chat List (Left Sidebar) ========== */
.chat-list {
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-right: 1px solid #e5e7eb;
}

.chat-list-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
  padding: 14px 16px;
}

.chat-list-header-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.chat-list-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chat-header-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  color: white !important;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.chat-header-btn i {
  color: white !important;
}

.chat-header-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-list-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.chat-list-body::-webkit-scrollbar {
  width: 5px;
}

.chat-list-body::-webkit-scrollbar-track {
  background: transparent;
}

.chat-list-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.chat-list-body::-webkit-scrollbar-thumb:hover {
  background: #00c444;
}

/* Conversation items */
.chat-list .list-group-item {
  border: none;
  border-bottom: 1px solid #f3f4f6;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.chat-list .list-group-item:hover {
  background: #f9fafb;
}

.chat-list .list-group-item.active {
  background: #ecfdf5;
  border-left: 3px solid #00c444;
  color: #1f2937;
}

.chat-list .list-group-item.active .fw-semibold {
  color: #047857;
}

.chat-list .list-group-item.active .text-muted {
  color: #4b5563 !important;
}

.chat-list .list-group-item .symbol img {
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  transition: border-color 0.15s ease;
}

.chat-list .list-group-item:hover .symbol img,
.chat-list .list-group-item.active .symbol img {
  border-color: #00c444;
}

.chat-list .badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 10px;
}

/* Unread conversation styling */
.chat-list .list-group-item.conv-unread {
  background: #f0fdf4;
  border-left: 3px solid #00c444;
}

.chat-list .list-group-item.conv-unread:hover {
  background: #dcfce7;
}

/* Unread dot indicator on avatar */
.conv-unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: #00c444;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 196, 68, 0.3);
}

/* Muted indicator icon beside title */
.conv-muted-icon {
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
  margin-left: 4px;
}

/* Conversation title text - handles truncation */
.conv-title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* RTL support for muted icon */
[dir="rtl"] .conv-muted-icon {
  margin-left: 0;
  margin-right: 4px;
}

/* Dark mode for muted icon */
[data-bs-theme="dark"] .conv-muted-icon {
  color: #9ca3af;
}

/* Unread preview text styling */
.conv-preview-unread {
  font-weight: 700;
  color: #374151 !important;
}

/* ========== Chat Panel (Right - Scrollable Content) ========== */
.chat-panel {
  background: #e8fff1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.chat-panel-inner {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Placeholder when no chat selected */
.chat-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
}

.chat-placeholder i {
  font-size: 56px;
  color: #00c444;
  opacity: 0.5;
  margin-bottom: 16px;
}

.chat-placeholder .fw-semibold {
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 4px;
}

.chat-placeholder div:last-child {
  color: #9ca3af;
  font-size: 14px;
}

/* ========== Responsive Design ========== */
@media (max-width: 1024px) {
  .chat-page-fullwidth {
    left: var(--bs-app-sidebar-width, 80px); /* Uses CSS variable */
  }

  [dir="rtl"] .chat-page-fullwidth,
  html[lang="ar"] .chat-page-fullwidth {
    left: 0;
    right: var(--bs-app-sidebar-width, 80px);
  }

  .chat-master {
    grid-template-columns: 280px 1fr;
  }

  .chat-list-header {
    padding: 12px 14px;
  }

  .chat-list .list-group-item {
    padding: 10px 14px;
  }
}

@media (max-width: 768px) {
  .chat-page-fullwidth {
    left: 0;
    top: 60px;
    padding: 0;
  }

  [dir="rtl"] .chat-page-fullwidth,
  html[lang="ar"] .chat-page-fullwidth {
    left: 0;
    right: 0;
  }

  .chat-master {
    display: flex;
    flex-direction: column;
    border-radius: 0;
  }

  .chat-list {
    flex-shrink: 0;
    height: auto;
    max-height: 220px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .chat-list-header {
    padding: 10px 12px;
  }

  .chat-list-header-title {
    font-size: 14px;
  }

  .chat-list-body {
    max-height: 160px;
  }

  .chat-list .list-group-item {
    padding: 8px 12px;
  }

  .chat-list .list-group-item .symbol {
    width: 36px !important;
    height: 36px !important;
  }

  .chat-panel {
    flex: 1;
    min-height: 0;
  }

  .chat-panel-header {
    padding: 10px 14px;
  }

  .chat-placeholder i {
    font-size: 40px;
  }

  .chat-placeholder .fw-semibold {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .chat-page-fullwidth {
    padding: 0;
  }

  .chat-list {
    max-height: 160px;
  }

  .chat-list-body {
    max-height: 110px;
  }

  .chat-header-btn {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}

/* =============================================
   DARK MODE STYLES - Master Layout
   ============================================= */
.chat-master-dark {
  background: #1a1a2e;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
}

/* Sidebar - Dark Mode */
.chat-master-dark .chat-list {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-master-dark .chat-list-header {
  background: linear-gradient(135deg, #0a5c36 0%, #064e2b 100%);
}

.chat-master-dark .chat-list-header .btn {
  background: rgba(255, 255, 255, 0.15);
}

.chat-master-dark .chat-list-header .btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.chat-master-dark .chat-list-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

/* Conversation Items - Dark Mode */
.chat-master-dark .chat-list .list-group-item {
  background: transparent;
  border-color: #2d2d44;
  color: #e5e5e5;
}

.chat-master-dark .chat-list .list-group-item:hover {
  background: #2d2d44;
}

.chat-master-dark .chat-list .list-group-item.active {
  background: #0d3320;
  border-left-color: #4ade80;
  color: #e5e5e5;
}

.chat-master-dark .chat-list .list-group-item.active .fw-semibold {
  color: #4ade80;
}

.chat-master-dark .chat-list .list-group-item.active .text-muted {
  color: #9ca3af !important;
}

.chat-master-dark .chat-list .list-group-item .fw-semibold {
  color: #e5e5e5;
}

.chat-master-dark .chat-list .list-group-item .text-muted {
  color: #9ca3af !important;
}

.chat-master-dark .chat-list .list-group-item .symbol img {
  border-color: #3d3d54;
}

.chat-master-dark .chat-list .list-group-item:hover .symbol img,
.chat-master-dark .chat-list .list-group-item.active .symbol img {
  border-color: #4ade80;
}

/* Chat Panel - Dark Mode */
.chat-master-dark .chat-panel {
  background: #0d1f15;
}

.chat-master-dark .chat-panel-header {
  background: rgba(26, 26, 46, 0.9);
  border-color: #2d2d44;
}

.chat-master-dark .chat-panel-header .fw-semibold {
  color: #e5e5e5;
}

.chat-master-dark .chat-panel-header .symbol img {
  border-color: #4ade80;
}

/* Placeholder - Dark Mode */
.chat-master-dark .chat-placeholder {
  color: #9ca3af;
}

.chat-master-dark .chat-placeholder i {
  color: #4ade80;
}

.chat-master-dark .chat-placeholder .fw-semibold {
  color: #9ca3af;
}

/* Loading & Error - Dark Mode */
.chat-master-dark .spinner-border {
  color: #4ade80 !important;
}

.chat-master-dark .text-muted {
  color: #9ca3af !important;
}

/* Responsive Dark Mode */
@media (max-width: 768px) {
  .chat-master-dark .chat-list {
    border-bottom-color: #2d2d44;
  }
}

/* ========================================
   Chat Actions Menu (Three-dots overflow)
   ======================================== */
.chat-actions-menu {
  position: relative;
  flex-shrink: 0;
}

.chat-menu-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.chat-menu-trigger:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.menu-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #ef4444;
  color: white;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #00c444;
}

.chat-menu-dropdown {
  position: fixed;
  top: auto;
  right: 24px;
  min-width: 200px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 6px 0;
  z-index: 9999;
  transform-origin: top right;
}

.chat-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.chat-menu-item:hover {
  background: #f0fdf4;
  color: #00c444;
}

.chat-menu-item i {
  font-size: 18px;
  width: 20px;
  text-align: center;
  color: #00c444;
  transition: color 0.15s ease;
}

.chat-menu-item:hover i {
  color: #047857;
}

.chat-menu-item span:first-of-type {
  flex: 1;
}

.chat-menu-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 6px 0;
}

[data-bs-theme="dark"] .chat-menu-divider {
  background: rgba(255, 255, 255, 0.1);
}

.menu-item-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #ef4444;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Menu transition */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: all 0.2s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Dark Mode Menu */
[data-bs-theme="dark"] .chat-menu-trigger {
  color: #9ca3af;
}

[data-bs-theme="dark"] .chat-menu-trigger:hover {
  background: #374151;
  color: #e5e5e5;
}

[data-bs-theme="dark"] .chat-menu-dropdown {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .chat-menu-item {
  color: #e5e5e5;
}

[data-bs-theme="dark"] .chat-menu-item:hover {
  background: #0d3320;
  color: #4ade80;
}

[data-bs-theme="dark"] .chat-menu-item i {
  color: #9ca3af;
}

[data-bs-theme="dark"] .chat-menu-item:hover i {
  color: #4ade80;
}

/* =============================================
   SEARCH FIELD
   ============================================= */
.chat-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.chat-search--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-search--clickable:hover {
  background: #f3f4f6;
}

.chat-search--clickable:active {
  background: #e5e7eb;
}

.chat-search__icon {
  color: #9ca3af;
  font-size: 14px;
  flex-shrink: 0;
}

.chat-search__placeholder {
  flex: 1;
  font-size: 13px;
  color: #9ca3af;
  min-width: 0;
  user-select: none;
}

.chat-search__input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #1f2937;
  outline: none;
  min-width: 0;
}

.chat-search__input::placeholder {
  color: #9ca3af;
}

.chat-search__clear {
  width: 22px;
  height: 22px;
  border: none;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.chat-search__clear:hover {
  background: #d1d5db;
  color: #374151;
}

/* Search spinner (message search loading) */
.chat-search__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-top-color: #198754;
  border-radius: 50%;
  animation: chat-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes chat-spin {
  to { transform: rotate(360deg); }
}

/* Search - Dark Mode */
.chat-master-dark .chat-search {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-master-dark .chat-search__input {
  color: #e5e5e5;
}

.chat-master-dark .chat-search__input::placeholder {
  color: #6b7280;
}

.chat-master-dark .chat-search__icon {
  color: #6b7280;
}

.chat-master-dark .chat-search__placeholder {
  color: #6b7280;
}

.chat-master-dark .chat-search--clickable:hover {
  background: #252540;
}

.chat-master-dark .chat-search--clickable:active {
  background: #2d2d44;
}

.chat-master-dark .chat-search__clear {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-master-dark .chat-search__spinner {
  border-color: #2d2d44;
  border-top-color: #4ade80;
}

/* Filter Tabs - Dark Mode */
.chat-master-dark .chat-filter-tabs {
  background: #1a1a2e;
  border-color: #2d2d44;
}

.chat-master-dark .chat-filter-tab {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-master-dark .chat-filter-tab:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

.chat-master-dark .chat-filter-tab.active {
  background: #00c444;
  color: white;
}

/* =============================================
   ALL CHATS MODAL (Flutter sync: _showAllConversations)
   ============================================= */
.all-chats-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.all-chats-dialog {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.all-chats-dialog__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.all-chats-dialog__header > i {
  font-size: 18px;
  color: #198754;
}

.all-chats-dialog__header > span {
  flex: 1;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.all-chats-dialog__close {
  width: 30px;
  height: 30px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.15s ease;
}

.all-chats-dialog__close:hover {
  background: #e5e7eb;
}

/* Search bar inside modal */
.all-chats-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.all-chats-search__icon {
  color: #9ca3af;
  font-size: 14px;
}

.all-chats-search__input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #111827;
  outline: none;
}

.all-chats-search__input::placeholder {
  color: #9ca3af;
}

/* Body */
.all-chats-dialog__body {
  flex: 1;
  overflow-y: auto;
}

/* States */
.all-chats-loading,
.all-chats-error,
.all-chats-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 14px;
}

.all-chats-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #198754;
  border-radius: 50%;
  animation: chat-spin 0.7s linear infinite;
}

.all-chats-error {
  color: #dc3545;
}

.all-chats-error button {
  padding: 6px 20px;
  border: none;
  background: #dc3545;
  color: #fff;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.all-chats-empty i {
  font-size: 36px;
  opacity: 0.4;
}

/* List */
.all-chats-list {
  padding: 4px 0;
}

.all-chats-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.all-chats-item:hover {
  background: #f9fafb;
}

.all-chats-item--deleted {
  opacity: 0.7;
}

.all-chats-item__avatar {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: visible;
  flex-shrink: 0;
}

.all-chats-item__avatar img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}

.all-chats-item__avatar-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #198754;
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.all-chats-item__deleted-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: #dc3545;
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

.all-chats-item__info {
  flex: 1;
  min-width: 0;
}

.all-chats-item__name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.all-chats-item__meta {
  margin-top: 2px;
  font-size: 12px;
}

.all-chats-item__deleted-label {
  color: #dc3545;
  font-weight: 500;
}

.all-chats-item__preview {
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Dark mode */
.chat-master-dark .all-chats-dialog {
  background: #1a1a2e;
}

.chat-master-dark .all-chats-dialog__header {
  border-color: #2d2d44;
}

.chat-master-dark .all-chats-dialog__header > span {
  color: #e5e5e5;
}

.chat-master-dark .all-chats-dialog__close {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-master-dark .all-chats-search {
  border-color: #2d2d44;
}

.chat-master-dark .all-chats-search__input {
  color: #e5e5e5;
}

.chat-master-dark .all-chats-item {
  border-color: #2d2d44;
}

.chat-master-dark .all-chats-item:hover {
  background: #12122a;
}

.chat-master-dark .all-chats-item__name {
  color: #e5e5e5;
}

.chat-master-dark .all-chats-item__avatar-placeholder {
  background: #2d6a4f;
}

.chat-master-dark .all-chats-item__deleted-badge {
  border-color: #1a1a2e;
}

/* =============================================
   ENHANCED PANEL HEADER
   ============================================= */
.chat-panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #d1fae5;
  padding: 10px 16px;
}

.chat-panel-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.chat-panel-header__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.chat-panel-header__name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-panel-header__status {
  font-size: 12px;
  color: #6b7280;
  font-weight: 400;
}

.chat-panel-header .symbol img {
  border: 2px solid #00c444;
  border-radius: 50%;
}

.chat-panel-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Panel Action Buttons */
.chat-panel-action-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: all 0.2s ease;
}

.chat-panel-action-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.chat-panel-action-btn.active,
.chat-panel-action-btn.active:hover {
  background: var(--chat-primary, #00c444);
  color: #ffffff;
}

.chat-panel-action-btn i.bi-shield {
  color: #00a038;
}

.chat-panel-action-btn--mia {
  background: transparent;
  color: var(--bs-body-color, #333);
}

.chat-panel-action-btn--mia:hover {
  background: var(--bs-tertiary-bg, #f5f5f5);
  color: var(--bs-body-color, #333);
}

.chat-panel-action-btn--mia .mia-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.mia-icon-small {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

/* =============================================
   MEMBER ACTIONS MODAL (Bottom Sheet Style)
   ============================================= */
.member-actions-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
}

.member-actions-modal {
  background: #ffffff;
  border-radius: 20px 20px 16px 16px;
  width: 100%;
  max-width: 420px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.2);
}

.member-actions-modal__header {
  padding: 12px 20px 16px;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.member-actions-modal__handle {
  width: 36px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
  margin: 0 auto 12px;
}

.member-actions-modal__title {
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.member-actions-modal__body {
  padding: 8px 12px;
}

.member-actions-modal__item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 12px;
  border: none;
  background: transparent;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: start;
}

.member-actions-modal__item:hover {
  background: #f9fafb;
}

.member-actions-modal__item:active {
  background: #f3f4f6;
  transform: scale(0.99);
}

.member-actions-modal__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.member-actions-modal__icon i {
  font-size: 20px;
  color: #6b7280;
}

.member-actions-modal__icon--muted {
  background: #dcfce7;
}

.member-actions-modal__icon--muted i {
  color: #16a34a;
}

.member-actions-modal__icon--danger {
  background: #fef2f2;
}

.member-actions-modal__icon--danger i {
  color: #dc2626;
}

.member-actions-modal__text {
  flex: 1;
  min-width: 0;
}

.member-actions-modal__label {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.member-actions-modal__desc {
  display: block;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.3;
}

.member-actions-modal__item--danger .member-actions-modal__label {
  color: #dc2626;
}

.member-actions-modal__arrow {
  font-size: 16px;
  color: #9ca3af;
  flex-shrink: 0;
}

.member-actions-modal__footer {
  padding: 12px;
  border-top: 1px solid #f3f4f6;
}

.member-actions-modal__cancel {
  width: 100%;
  padding: 14px;
  border: none;
  background: #f3f4f6;
  color: #374151;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.member-actions-modal__cancel:hover {
  background: #e5e7eb;
}

.member-actions-modal__cancel:active {
  background: #d1d5db;
  transform: scale(0.99);
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-slide-leave-active {
  transition: transform 0.2s ease-in;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}

/* Dark mode for member actions modal */
.chat-master-dark .member-actions-modal {
  background: #1f2937;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5);
}

.chat-master-dark .member-actions-modal__header {
  border-color: #374151;
}

.chat-master-dark .member-actions-modal__handle {
  background: #4b5563;
}

.chat-master-dark .member-actions-modal__title {
  color: #f3f4f6;
}

.chat-master-dark .member-actions-modal__item:hover {
  background: #374151;
}

.chat-master-dark .member-actions-modal__item:active {
  background: #4b5563;
}

.chat-master-dark .member-actions-modal__icon {
  background: #374151;
}

.chat-master-dark .member-actions-modal__icon i {
  color: #9ca3af;
}

.chat-master-dark .member-actions-modal__icon--muted {
  background: rgba(22, 163, 74, 0.2);
}

.chat-master-dark .member-actions-modal__icon--muted i {
  color: #4ade80;
}

.chat-master-dark .member-actions-modal__icon--danger {
  background: rgba(220, 38, 38, 0.2);
}

.chat-master-dark .member-actions-modal__icon--danger i {
  color: #f87171;
}

.chat-master-dark .member-actions-modal__label {
  color: #f3f4f6;
}

.chat-master-dark .member-actions-modal__desc {
  color: #9ca3af;
}

.chat-master-dark .member-actions-modal__item--danger .member-actions-modal__label {
  color: #f87171;
}

.chat-master-dark .member-actions-modal__arrow {
  color: #6b7280;
}

.chat-master-dark .member-actions-modal__footer {
  border-color: #374151;
}

.chat-master-dark .member-actions-modal__cancel {
  background: #374151;
  color: #f3f4f6;
}

.chat-master-dark .member-actions-modal__cancel:hover {
  background: #4b5563;
}

.chat-master-dark .member-actions-modal__cancel:active {
  background: #6b7280;
}

/* =============================================
   MIA SUMMARY DIALOG (shared styles)
   ============================================= */
.chat-summary-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.chat-summary-dialog {
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: summary-dialog-enter 0.3s ease-out;
}

@keyframes summary-dialog-enter {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.chat-summary-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 28px;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.chat-summary-dialog__header .mia-icon-small {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.chat-summary-dialog__header span {
  flex: 1;
}

.chat-summary-dialog__close {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.15s;
}

.chat-summary-dialog__close i {
  color: white !important;
}

.chat-summary-dialog__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-summary-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.chat-summary-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.chat-summary-chip {
  padding: 8px 18px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-summary-chip:hover {
  border-color: #00c444;
  color: #00a038;
}

.chat-summary-chip.active {
  background: #00c444;
  color: white;
  border-color: #00c444;
  font-weight: 600;
}

.chat-summary-dates {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.chat-summary-date-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.chat-summary-date-label i {
  color: #00c444;
  font-size: 16px;
}

.chat-summary-date-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  outline: none;
  transition: border-color 0.15s;
}

.chat-summary-date-input:focus {
  border-color: #00c444;
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.chat-summary-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
  color: #6b7280;
  font-size: 14px;
}

.chat-summary-result {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 2px solid #6ee7b7;
  border-radius: 16px;
  overflow: hidden;
  animation: result-fade-in 0.4s ease-out;
}

@keyframes result-fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-summary-result__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-bottom: 1px solid #6ee7b7;
}

.chat-summary-result__title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-summary-result__title > i,
.chat-summary-result__title > i.bi,
.chat-summary-result__title > i.fas {
  width: 42px !important;
  height: 42px !important;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 18px !important;
  flex-shrink: 0;
}

.chat-summary-result__title-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.chat-summary-result__title-text > span:first-child {
  font-weight: 700;
  color: #065f46;
  font-size: 16px;
}

.chat-summary-result__count {
  font-size: 13px;
  color: #047857;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.chat-summary-result__count::before {
  content: "";
  width: 6px;
  height: 6px;
  background: #00c444;
  border-radius: 50%;
}

.chat-summary-result__actions {
  display: flex;
  gap: 8px;
}

.chat-summary-action-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  border: none !important;
  background: linear-gradient(
    135deg,
    rgba(0, 196, 68, 0.15) 0%,
    rgba(0, 160, 56, 0.15) 100%
  ) !important;
  color: #fff !important;
  border-radius: 10px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 16px !important;
  transition: all 0.2s ease !important;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.15) !important;
  padding: 0 !important;
  flex-shrink: 0 !important;
}

.chat-summary-action-btn:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%) !important;
  color: white !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.3) !important;
}

.chat-summary-action-btn:active {
  transform: translateY(0) !important;
}

.chat-summary-action-btn i {
  font-size: 16px !important;
  color: inherit !important;
}

.chat-summary-result__text {
  padding: 20px;
  font-size: 15px;
  line-height: 1.8;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #00c444 #ecfdf5;
}

.chat-summary-result__text::-webkit-scrollbar {
  width: 6px;
}

.chat-summary-result__text::-webkit-scrollbar-track {
  background: #ecfdf5;
  border-radius: 3px;
}

.chat-summary-result__text::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  border-radius: 3px;
}

.chat-summary-result__footer {
  padding: 14px 20px;
  border-top: 1px solid #6ee7b7;
  background: rgba(209, 250, 229, 0.5);
}

.chat-summary-mia-badge {
  display: inline-flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 10px 16px !important;
  background: linear-gradient(
    135deg,
    rgba(0, 196, 68, 0.15) 0%,
    rgba(0, 160, 56, 0.15) 100%
  ) !important;
  border-radius: 24px !important;
  font-size: 14px !important;
  color: #fff !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.1) !important;
}

.chat-summary-mia-badge i {
  font-size: 16px !important;
  color: #00c444 !important;
}

.chat-summary-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  color: #dc2626;
  font-size: 14px;
}

.chat-summary-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
}

.chat-summary-btn--cancel {
  padding: 10px 20px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.chat-summary-btn--cancel:hover {
  background: #f9fafb;
  color: #374151;
}

.chat-summary-btn--submit {
  padding: 10px 20px;
  border: none;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.chat-summary-btn--submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a038 0%, #008830 100%);
}

.chat-summary-btn--submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-summary-btn--submit i {
  color: white !important;
}

/* =============================================
   MEMBERS MODAL
   ============================================= */
.chat-members-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.chat-members-dialog {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 680px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-members-dialog__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
}

.chat-members-dialog__header i.bi-people-fill {
  font-size: 20px;
}

.chat-members-dialog__header span {
  flex: 1;
}

.chat-members-dialog__close {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.15s;
}

.chat-members-dialog__close i {
  color: white !important;
}

.chat-members-dialog__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-members-dialog__body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.chat-members-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  color: #6b7280;
  font-size: 14px;
}

.chat-members-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #dc2626;
  font-size: 14px;
}

.chat-members-error i {
  font-size: 20px;
  flex-shrink: 0;
}

.chat-members-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  color: #9ca3af;
  font-size: 14px;
}

.chat-members-empty i {
  font-size: 40px;
  opacity: 0.5;
}

.chat-members-header {
  padding: 0 0 16px 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.chat-members-header__info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.chat-members-header__type {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #047857;
}

.chat-members-header__type i {
  font-size: 16px;
}

.chat-members-header__count {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f3f4f6;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.chat-members-header__count i {
  font-size: 15px;
  color: #00c444;
}

.chat-members-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.chat-member-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  transition: all 0.2s ease;
  cursor: default;
}

.chat-member-card:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.chat-member-card--current {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #00c444;
  border-width: 2px;
}

.chat-member-card--current:hover {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #00a038;
}

.chat-member-card--admin {
  border-color: #fbbf24;
}

.chat-member-card__avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.chat-member-card__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e5e7eb;
}

.chat-member-card--current .chat-member-card__avatar {
  border-color: #00c444;
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.1);
}

.chat-member-card--admin .chat-member-card__avatar {
  border-color: #fbbf24;
}

.chat-member-card__admin-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
}

.chat-member-card__info {
  flex: 1;
  min-width: 0;
}

.chat-member-card__name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-member-card__email {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-member-card__badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chat-member-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.chat-member-badge--you {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  gap: 4px;
}

.chat-member-badge--you i {
  font-size: 11px;
}

.chat-member-badge--admin {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #b45309;
  gap: 4px;
}

.chat-member-badge--admin i {
  font-size: 11px;
}

/* Selectable Member Cards (for kick/add modes) */
.chat-member-card--selectable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-member-card--selectable:hover {
  background: #f0fdf4;
  border-color: #22c55e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);
}

.chat-member-card--selectable.chat-member-card--danger:hover {
  background: #fef2f2;
  border-color: #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
}

.chat-member-card__action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chat-member-card__action--add {
  background: #dcfce7;
  color: #16a34a;
}

.chat-member-card__action--add i {
  font-size: 18px;
}

.chat-member-card__action--kick {
  background: #fee2e2;
  color: #dc2626;
}

.chat-member-card__action--kick i {
  font-size: 18px;
}

/* Dark mode for selectable cards */
.chat-master-dark .chat-member-card--selectable:hover,
[data-bs-theme="dark"] .chat-member-card--selectable:hover {
  background: rgba(34, 197, 94, 0.1);
  border-color: #22c55e;
}

.chat-master-dark .chat-member-card--selectable.chat-member-card--danger:hover,
[data-bs-theme="dark"] .chat-member-card--selectable.chat-member-card--danger:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

.chat-master-dark .chat-member-card__action--add,
[data-bs-theme="dark"] .chat-member-card__action--add {
  background: rgba(22, 163, 74, 0.2);
  color: #4ade80;
}

.chat-master-dark .chat-member-card__action--kick,
[data-bs-theme="dark"] .chat-member-card__action--kick {
  background: rgba(220, 38, 38, 0.2);
  color: #f87171;
}

/* Members Modal - Dark Mode */
.chat-master-dark .chat-members-dialog,
[data-bs-theme="dark"] .chat-members-dialog {
  background: #1f2937;
}

.chat-master-dark .chat-members-dialog__body,
[data-bs-theme="dark"] .chat-members-dialog__body {
  color: #e5e5e5;
}

.chat-master-dark .chat-members-error,
[data-bs-theme="dark"] .chat-members-error {
  background: #3d1515;
  border-color: #7f1d1d;
  color: #f87171;
}

.chat-master-dark .chat-members-header,
[data-bs-theme="dark"] .chat-members-header {
  border-color: #374151;
}

.chat-master-dark .chat-members-header__type,
[data-bs-theme="dark"] .chat-members-header__type {
  background: linear-gradient(135deg, #0d3320 0%, #134e2a 100%);
  color: #4ade80;
}

.chat-master-dark .chat-members-header__count,
[data-bs-theme="dark"] .chat-members-header__count {
  background: #374151;
  color: #9ca3af;
}

.chat-master-dark .chat-members-header__count i,
[data-bs-theme="dark"] .chat-members-header__count i {
  color: #4ade80;
}

.chat-master-dark .chat-member-card,
[data-bs-theme="dark"] .chat-member-card {
  background: #2d2d44;
  border-color: #3d3d54;
}

.chat-master-dark .chat-member-card:hover,
[data-bs-theme="dark"] .chat-member-card:hover {
  background: #374151;
  border-color: #4b5563;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.chat-master-dark .chat-member-card--current,
[data-bs-theme="dark"] .chat-member-card--current {
  background: linear-gradient(135deg, #0d3320 0%, #134e2a 100%);
  border-color: #4ade80;
}

.chat-master-dark .chat-member-card--current:hover,
[data-bs-theme="dark"] .chat-member-card--current:hover {
  background: linear-gradient(135deg, #134e2a 0%, #166534 100%);
  border-color: #22c55e;
}

.chat-master-dark .chat-member-card--admin,
[data-bs-theme="dark"] .chat-member-card--admin {
  border-color: #fbbf24;
}

.chat-master-dark .chat-member-card__avatar,
[data-bs-theme="dark"] .chat-member-card__avatar {
  border-color: #3d3d54;
}

.chat-master-dark .chat-member-card--current .chat-member-card__avatar,
[data-bs-theme="dark"] .chat-member-card--current .chat-member-card__avatar {
  border-color: #4ade80;
  box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.2);
}

.chat-master-dark .chat-member-card--admin .chat-member-card__avatar,
[data-bs-theme="dark"] .chat-member-card--admin .chat-member-card__avatar {
  border-color: #fbbf24;
}

.chat-master-dark .chat-member-card__admin-badge,
[data-bs-theme="dark"] .chat-member-card__admin-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-color: #1f2937;
}

.chat-master-dark .chat-member-card__name,
[data-bs-theme="dark"] .chat-member-card__name {
  color: #e5e5e5;
}

.chat-master-dark .chat-member-card__email,
[data-bs-theme="dark"] .chat-member-card__email {
  color: #9ca3af;
}

.chat-master-dark .chat-member-badge--you,
[data-bs-theme="dark"] .chat-member-badge--you {
  background: linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%);
  color: #60a5fa;
}

.chat-master-dark .chat-member-badge--admin,
[data-bs-theme="dark"] .chat-member-badge--admin {
  background: linear-gradient(135deg, #422006 0%, #78350f 100%);
  color: #fbbf24;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* =============================================
   DARK MODE - Panel Header & New Features
   ============================================= */
.chat-master-dark .chat-panel-header {
  background: rgba(26, 26, 46, 0.9);
  border-color: #2d2d44;
}

.chat-master-dark .chat-panel-header__name {
  color: #e5e5e5;
}

.chat-master-dark .chat-panel-header__status {
  color: #9ca3af;
}

.chat-master-dark .chat-panel-header .symbol img {
  border-color: #4ade80;
}

.chat-master-dark .chat-panel-action-btn {
  background: #2d2d44;
  color: #9ca3af;
}

.chat-master-dark .chat-panel-action-btn:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

/* Dark mode dropdown styles are in the unscoped block below */

/* Summary Dialog - Dark Mode */
.chat-master-dark .chat-summary-dialog,
[data-bs-theme="dark"] .chat-summary-dialog {
  background: #1f2937;
}

.chat-master-dark .chat-summary-dialog__body,
[data-bs-theme="dark"] .chat-summary-dialog__body {
  color: #e5e5e5;
}

.chat-master-dark .chat-summary-chip,
[data-bs-theme="dark"] .chat-summary-chip {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

.chat-master-dark .chat-summary-chip:hover,
[data-bs-theme="dark"] .chat-summary-chip:hover {
  border-color: #00c444;
  color: #4ade80;
}

.chat-master-dark .chat-summary-chip.active,
[data-bs-theme="dark"] .chat-summary-chip.active {
  background: #00c444;
  border-color: #00c444;
  color: white;
}

.chat-master-dark .chat-summary-date-label,
[data-bs-theme="dark"] .chat-summary-date-label {
  color: #d1d5db;
}

.chat-master-dark .chat-summary-date-input,
[data-bs-theme="dark"] .chat-summary-date-input {
  background: #374151;
  border-color: #4b5563;
  color: #e5e5e5;
}

.chat-master-dark .chat-summary-result,
[data-bs-theme="dark"] .chat-summary-result {
  background: linear-gradient(135deg, #14532d 0%, #166534 100%);
  border-color: #22c55e;
}

.chat-master-dark .chat-summary-result__header,
[data-bs-theme="dark"] .chat-summary-result__header {
  background: linear-gradient(135deg, #166534 0%, #15803d 100%);
  border-color: #22c55e;
}

.chat-master-dark .chat-summary-result__title > i,
[data-bs-theme="dark"] .chat-summary-result__title > i {
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
}

.chat-master-dark .chat-summary-result__title-text > span:first-child,
[data-bs-theme="dark"] .chat-summary-result__title-text > span:first-child {
  color: #f0fdf4;
}

.chat-master-dark .chat-summary-result__count,
[data-bs-theme="dark"] .chat-summary-result__count {
  color: #86efac;
}

.chat-master-dark .chat-summary-result__count::before,
[data-bs-theme="dark"] .chat-summary-result__count::before {
  background: #4ade80;
}

.chat-master-dark .chat-summary-action-btn,
[data-bs-theme="dark"] .chat-summary-action-btn {
  background: linear-gradient(
    135deg,
    rgba(0, 196, 68, 0.25) 0%,
    rgba(0, 160, 56, 0.25) 100%
  );
  color: #86efac;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.2);
}

.chat-master-dark .chat-summary-action-btn:hover,
[data-bs-theme="dark"] .chat-summary-action-btn:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a038 100%);
  color: white;
}

.chat-master-dark .chat-summary-result__text,
[data-bs-theme="dark"] .chat-summary-result__text {
  color: #e5e7eb;
  scrollbar-color: #00c444 #14532d;
}

.chat-master-dark .chat-summary-result__text::-webkit-scrollbar-track,
[data-bs-theme="dark"] .chat-summary-result__text::-webkit-scrollbar-track {
  background: #14532d;
}

.chat-master-dark .chat-summary-result__footer,
[data-bs-theme="dark"] .chat-summary-result__footer {
  border-color: #22c55e;
  background: rgba(22, 101, 52, 0.5);
}

.chat-master-dark .chat-summary-mia-badge,
[data-bs-theme="dark"] .chat-summary-mia-badge {
  background: linear-gradient(
    135deg,
    rgba(0, 196, 68, 0.25) 0%,
    rgba(0, 160, 56, 0.25) 100%
  );
  color: #86efac;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.15);
}

.chat-master-dark .chat-summary-mia-badge i,
[data-bs-theme="dark"] .chat-summary-mia-badge i {
  color: #4ade80;
}

.chat-master-dark .chat-summary-dialog__footer,
[data-bs-theme="dark"] .chat-summary-dialog__footer {
  border-color: #374151;
}

.chat-master-dark .chat-summary-btn--cancel,
[data-bs-theme="dark"] .chat-summary-btn--cancel {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}
</style>

<!-- Unscoped styles for teleported dropdown (rendered at body level) -->
<style>
.chat-panel-dropdown {
  min-width: 180px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 0;
  transform-origin: top right;
}

.chat-panel-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.chat-panel-dropdown-item:hover {
  background: #f0fdf4;
  color: #00c444;
}

.chat-panel-dropdown-item i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.chat-panel-dropdown-item--danger {
  color: #dc2626;
}

.chat-panel-dropdown-item--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.chat-panel-dropdown-item--success {
  color: #16a34a;
}

.chat-panel-dropdown-item--success:hover {
  background: #f0fdf4;
  color: #16a34a;
}

.chat-panel-dropdown-item--muted {
  color: #6b7280;
  background: #f9fafb;
}

.chat-panel-dropdown-item--muted:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Dark mode */
[data-bs-theme="dark"] .chat-panel-dropdown {
  background: #1f2937;
  border-color: #374151;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item {
  color: #e5e5e5;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item:hover {
  background: #0d3320;
  color: #4ade80;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item--danger {
  color: #f87171;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item--danger:hover {
  background: #3d1515;
  color: #f87171;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item--muted {
  color: #9ca3af;
  background: #2d2d44;
}

[data-bs-theme="dark"] .chat-panel-dropdown-item--muted:hover {
  background: #3d3d54;
  color: #e5e5e5;
}

[data-bs-theme="dark"] .conv-muted-badge {
  background: #4b5563;
  border-color: #1f2937;
}

/* Transition for teleported dropdown */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
